/**
 * @kudoz/api-client
 *
 * Typed HTTP client for the Kudoz/QuickPost API surface.
 * Platform-agnostic — no browser or React Native APIs are imported here.
 * Platform coupling is injected via the `TokenStorage`, `attributionProvider`,
 * and `partnerHeadersProvider` fields of `ApiClientConfig`.
 *
 * Codegen approach (decision pinned in replit.md):
 *   Hand-written against `packages/shared/src/schema.ts` types. A Zod
 *   route registry would save boilerplate but adds complexity before we have
 *   the full 421-endpoint surface. We revisit codegen when Phase 2 lands.
 *
 * Phase 1 typed surface: auth, user, partner.posts, partner.menu,
 *   partner.restaurants, partner.notifications, partner.dashboard,
 *   partner.social, account.
 * All other endpoints are accessible via `client.raw(...)`.
 *
 * See RFC §4.2 and §6 for the contract rules this package enforces.
 */
import {
  redactAttributionContext,
  redactSensitive,
} from "@kudoz/shared/redactDiagnostics";
import type {
  SmartFixEvaluationIdList,
  SmartFixPublicationCorrelationList,
} from "@kudoz/shared/smartFixEvaluation";
import type { LinkDeliveryStatus } from "@kudoz/shared/linkDelivery";

// The mobile and web clients consume the exact same durable delivery
// projection as the server. Keep pin results and required recovery evidence in
// this single contract rather than maintaining a lossy API-client copy.
export type { LinkDeliveryStatus } from "@kudoz/shared/linkDelivery";

// Shared SmartFix telemetry contract. Re-exporting the types here keeps native
// callers on the same strict metadata-only wire shape without adding a second
// hand-written copy in each platform client.
export type {
  SmartFixAssessment,
  SmartFixCaptureMode,
  SmartFixDimension,
  SmartFixEvidence,
  SmartFixEvent,
  SmartFixEventKind,
  SmartFixIngestionRequest,
  SmartFixTonePatch,
  SmartFixEvaluation,
  SmartFixPublicationProof,
  SmartFixEvaluationIdList,
  SmartFixPublicationCorrelation,
  SmartFixPublicationCorrelationList,
  SmartFixRetainedPatchState,
  SmartFixPreferenceReason,
  SmartFixVerification,
  SmartFixVerdict,
} from "@kudoz/shared/smartFixEvaluation";

// ─────────────────────────────────────────────────────────────────────────────
// Core interfaces
// ─────────────────────────────────────────────────────────────────────────────

/**
 * An axis-aligned crop rectangle in integer pixels. Mirrors `CropRect` in
 * `packages/shared/src/types/photoEditSpec.ts`; redeclared here so the
 * platform-agnostic api-client carries no `@shared` runtime dependency.
 */
export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Platform-specific token persistence.
 * Web: localStorage + sessionStorage.
 * RN:  expo-secure-store (Keychain on iOS, EncryptedSharedPreferences on Android).
 *
 * JWTs in AsyncStorage are explicitly forbidden (RFC §4.5 anti-patterns).
 */
export interface TokenStorage {
  getKudozToken(): Promise<string | null>;
  setKudozToken(token: string | null): Promise<void>;

  getEatzyToken(): Promise<string | null>;
  setEatzyToken(token: string | null): Promise<void>;

  getKudozRefreshToken(): Promise<string | null>;
  setKudozRefreshToken(token: string | null): Promise<void>;

  getEatzyRefreshToken(): Promise<string | null>;
  setEatzyRefreshToken(token: string | null): Promise<void>;

  /** Unix timestamp (seconds) when the stored Eatzy access token expires. */
  getEatzyTokenExpiry(): Promise<number | null>;
  setEatzyTokenExpiry(expiry: number | null): Promise<void>;

  /** Unix timestamp (seconds) when the stored Eatzy refresh token expires. */
  getEatzyRefreshExpiry(): Promise<number | null>;
  setEatzyRefreshExpiry(expiry: number | null): Promise<void>;

  clear(): Promise<void>;
}

/**
 * Attribution payload sent in the `x-attribution-context` header.
 * Mirrors the shape normalised by `attributionContextMiddleware`.
 */
export interface AttributionContext {
  source?: string;
  channel?: string;
  short_link?: string;
  post_id?: string;
  review_id?: string;
  restaurant_id?: string;
  campaign_id?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  session_id?: string;
  ref?: string;
}

/**
 * One observed HTTP request, emitted via {@link ApiClientConfig.requestObserver}
 * after the request settles (success, non-2xx, or transport failure).
 *
 * Deliberately carries NO request/response bodies, NO auth tokens, and NO
 * query strings — only the method, path, status, timing, the server-issued
 * request id (`X-Request-Id`), and a coarse error code. This is safe to feed
 * straight into the on-device diagnostics ring buffer (Task #2644).
 */
export interface RequestObserverEvent {
  /** Uppercased HTTP method, e.g. "GET" / "POST". */
  method: string;
  /** Request path only (no query string), e.g. "/api/partner/posts". */
  path: string;
  /** HTTP status code, or 0 when the request never produced a response. */
  status: number;
  /** Wall-clock duration from request start to settle, in milliseconds. */
  durationMs: number;
  /** Value of the server `X-Request-Id` response header, or null. */
  requestId: string | null;
  /** Coarse error code (e.g. "NETWORK_REQUEST_FAILED"), or null on success. */
  errorCode: string | null;
}

export interface ApiClientConfig {
  /**
   * Base URL for the Kudoz API server, e.g. `https://app.eatzy.com` or
   * `http://localhost:5000`. No trailing slash.
   */
  baseUrl: string;

  /** Platform-supplied token storage. */
  storage: TokenStorage;

  /**
   * Returns the current attribution context, or null if none is active.
   * Called automatically on every write request (POST / PUT / PATCH).
   */
  attributionProvider?: () => AttributionContext | null;

  /**
   * Returns additional partner headers (`x-partner-user-id`,
   * `x-restaurant-id`, `x-eatzy-token`) if the current session is a
   * partner session. Called on every `/api/partner/` request.
   *
   * Web impl reads from `localStorage` / `sessionStorage`.
   * RN impl reads from `expo-secure-store`.
   */
  partnerHeadersProvider?: () => Record<string, string>;

  /**
   * Called when a token refresh fails and the session is unrecoverable.
   * Web: dispatches `auth:expired` event.
   * RN:  navigates to the sign-in screen via `expo-router`.
   */
  onAuthExpired?: () => void;

  /**
   * Value sent as the `X-Client` header on every request.
   * e.g. `kudoz-web/0.1.0` or `kudoz-partner-mobile/1.0.0 (ios)`.
   * See RFC §6.2.
   */
  clientHeader: string;

  /**
   * Injectable fetch implementation. Defaults to `globalThis.fetch`.
   * Useful for testing (pass a mock fetch) without requiring network access.
   */
  fetch?: typeof globalThis.fetch;

  /**
   * Optional observer invoked once per request after it settles, with a
   * body-free, token-free {@link RequestObserverEvent}. The RN app feeds this
   * into its diagnostics ring buffer so a shared, redacted network breadcrumb
   * trail can ship in a device diagnostics bundle (Task #2644). Never throws
   * back into the caller — exceptions from the observer are swallowed.
   */
  requestObserver?: (event: RequestObserverEvent) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 1 response types (hand-written from schema.ts)
// ─────────────────────────────────────────────────────────────────────────────

export interface ApiUser {
  id: string;
  canonicalId?: string;
  email: string;
  name: string;
  avatar?: string;
  bio?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  kudozBalance?: number;
  lifetimeKudozEarned?: number;
  accountBalance?: string;
  trustScore?: number;
  trustTier?: "newbie" | "reliable" | "trusted" | "elite";
  totalReviews?: number;
  totalOrders?: number;
  profileCompletionPercent?: number;
  followersCount?: number;
  followingCount?: number;
  isGuestAccount?: boolean;
}

export interface AuthSignInResponse {
  token: string;
  user: ApiUser;
  eatzyAccessToken?: string;
  eatzyRefreshToken?: string;
  eatzyTokenExpires?: number;
  eatzyRefreshExpires?: number;
  /**
   * Task #2776 — set by the server when the sign-in succeeded but the
   * Eatzy access-token mint failed, so the device has NO Eatzy token. The
   * client uses this signal to mint one from the partner's credentials
   * (see `AuthModule.mintEatzyToken`) instead of silently sending no
   * `x-eatzy-token` and seeing "0 active restaurants".
   */
  eatzyTokenMintFailed?: boolean;
}

/**
 * Response shape of `POST /api/partner/restmanager/auth` — an unauthenticated
 * credential-based Eatzy access-token mint. Note the access token field is
 * `accessToken` here (NOT `eatzyAccessToken` as in the sign-in response).
 */
export interface RestmanagerAuthResponse {
  success: boolean;
  accessToken?: string;
  roles?: Record<string, string>;
  eatzyRefreshToken?: string;
  eatzyTokenExpires?: number;
  eatzyRefreshExpires?: number;
  error?: string;
}

export interface AuthRefreshKudozResponse {
  token: string;
  user?: Partial<ApiUser>;
}

export interface AuthRefreshEatzyResponse {
  eatzyAccessToken: string;
  eatzyTokenExpires?: number;
}

export interface AuthSessionResponse {
  user: ApiUser;
  session: Record<string, unknown>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Partner types
// ─────────────────────────────────────────────────────────────────────────────

export interface PartnerRestaurant {
  /**
   * Primary restaurant identifier used by the mobile app. This is the Kudoz
   * restaurant UUID when the restaurant has a local record, otherwise the
   * stringified Eatzy numeric id. Server requests are scoped by
   * `kudozExternalId` (the `x-restaurant-id` header), so unlinked restaurants
   * are auto-provisioned server-side on first use.
   */
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  logoImageUrl: string | null;
  /**
   * Restaurant Manager API does not return a per-restaurant role, so this
   * defaults to the least-privileged "staff" for the list (so capability
   * fallbacks never over-grant). The authoritative role for the active
   * restaurant comes from `GET /api/partner/me`.
   */
  partnerRole: string;
  /**
   * Eatzy client identifier (the `profile` slug) used as the profile in
   * `GET /api/partner/menu/:profile`. Public menu URLs must instead use the
   * canonical native id from `kudozExternalId`.
   * Null when the restaurant is not linked to Eatzy.
   */
  eatzyClientId: string | null;
  /**
   * Eatzy numeric restaurant id. Sent as the `x-restaurant-id` header so the
   * server can scope (and auto-provision) data without a UUID → numeric-id
   * lookup. Null when the restaurant is not linked to Eatzy.
   */
  kudozExternalId: number | null;
  /**
   * IANA timezone name (e.g. "Australia/Sydney") for schedule-post formatting.
   * Null when the restaurant has not set a timezone.
   */
  eatzyTimezone?: string | null;
  /** Whether the restaurant is currently active on Eatzy. */
  isActive: boolean;
}

export interface PartnerDashboardStats {
  totalPosts?: number;
  postsToday?: number;
  pendingApproval?: number;
  failedPosts?: number;
  [key: string]: unknown;
}

export interface SocialPostStatus {
  platform: string;
  status: string;
  platformPostId?: string | null;
  platformPostIdProvenance?: string | null;
  provenance?: string | null;
  errorMessage?: string | null;
  /** Partner-facing mapped error headline, when the status endpoint supplies one. */
  plainEnglishError?: string | null;
  /** Optional mapped detail sentence for the partner-facing error UI. */
  plainEnglishDetail?: string | null;
  /** Whether the UI should offer Reconnect instead of Retry. */
  suggestReconnect?: boolean;
  publishedAt?: string | null;
  warningCode?: string | null;
  warningMessage?: string | null;
  recoveryAction?:
    | "reconnect"
    | "retry"
    | "refresh_health"
    | "add_comment_manually"
    | null;
  /**
   * Durable outcome for the separately-delivered destination (normally the
   * Facebook/Instagram first-comment link). Post publication and destination
   * delivery are independent: a live post can still have a pending, failed,
   * omitted, or unknown link-delivery outcome.
   */
  linkDelivery?: LinkDeliveryStatus | null;
}

export interface PartnerPost {
  /**
   * Server-assigned post id. For freshly-created partner posts this is a
   * string of the form `partner-post-<uuid>`; some legacy list rows still
   * surface a numeric id, so the type is intentionally wide.
   */
  id: string | number;
  restaurantId: number | string;
  userId?: string;
  caption?: string | null;
  instagramCaption?: string | null;
  facebookCaption?: string | null;
  tiktokCaption?: string | null;
  eatzyCaption?: string | null;
  mediaUrls?: string[];
  menuItemId?: number | null;
  status?: string;
  postType?: string;
  createdAt?: string;
  updatedAt?: string;
  scheduledAt?: string | null;
  /**
   * Per-platform cross-post status. NOT returned by `POST /api/partner/posts`
   * (the cross-post fans out asynchronously after the response). Fetch the
   * latest statuses via `partner.posts.getSocialStatus(postId)`.
   */
  socialPosts?: SocialPostStatus[];
  [key: string]: unknown;
}

/**
 * Wire response for `POST /api/partner/posts`.
 *
 * The server fans out cross-posts asynchronously after this response is sent,
 * so per-platform statuses are NOT included here — callers that need them
 * must poll `partner.posts.getSocialStatus(id)`.
 */
export interface PartnerPostCreateResponse {
  /** `partner-post-<uuid>`. */
  id: string;
  success: boolean;
  /** `'published' | 'pending_approval' | 'scheduled'`. */
  status: string;
  /** Set when the post body included a `link`. */
  shortLink?: string;
  scheduled?: boolean;
  scheduledFor?: string;
  seriesId?: string;
  count?: number;
  message?: string;
}

export interface PartnerPostsListResponse {
  posts: PartnerPost[];
  total?: number;
  hasMore?: boolean;
}

/**
 * Wire response for `POST /api/partner/posts/:postId/approve`.
 * Server marks the post as published and triggers cross-posting.
 */
export interface PartnerPostApproveResponse {
  success: boolean;
  status: string;
}

/**
 * Wire response for `POST /api/partner/posts/:postId/reject`.
 * Server stores the rejection note on the post and notifies the submitter.
 */
export interface PartnerPostRejectResponse {
  success: boolean;
  status: string;
  note: string;
}

/**
 * Wire body for `POST /api/partner/posts`.
 *
 * `restaurantId` is resolved server-side from the partner-auth session;
 * extra fields are stripped by the server's Zod schema. The two required
 * fields are `promotionType` and either `text` or `caption`.
 */
export interface PartnerPostCreateBody {
  /**
   * The restaurant the post belongs to. The server's partner auth layer
   * resolves the active restaurant from the session and accepts either a
   * numeric Eatzy ID or a UUID string in the body — partner-mobile uses
   * the UUID form (see RestaurantContext), the web composer uses the
   * numeric form.
   */
  restaurantId?: number | string;
  /** Server-required. One of the enum values in `createPartnerPostSchema`. */
  promotionType: string;
  /** Either `text` or `caption` is required by the server. */
  text?: string;
  caption?: string;
  /** Cross-post target platforms (server alias `platformTargets`). */
  crossPostPlatforms?: Array<
    "eatzy" | "instagram" | "facebook" | "tiktok" | "instagram_story"
  >;
  /** Per-platform caption overrides (server forwards these to the cross-poster). */
  platformOverrides?: Record<string, string>;
  /** General media attached to the post. */
  media?: Array<{ url: string; type: "image" | "video" }>;
  /** Optional human-readable promo title. */
  promotionTitle?: string;
  /** Optional public deep-link that the server short-links and injects. */
  link: string;
  /** ISO datetime to schedule the post for. */
  scheduledFor?: string;
  /**
   * Video posts (Task #1008): object-storage URL of the uploaded master clip.
   */
  videoMasterUrl?: string;
  /**
   * Video posts (Task #1008): trim window + cover-frame offset chosen in the
   * native VideoTrim UI.
   */
  videoTrimSpec?: VideoTrimSpec;
  /**
   * Video posts (Task #1029): per-platform transcoded encodings returned by
   * `/api/partner/video/transcode`. Attached so the cross-poster can pick
   * the right asset for IG / FB / TikTok / Eatzy without re-running ffmpeg.
   */
  videoEncodings?: VideoPlatformEncoding[];
  /** Object-storage URL of the cover-frame JPEG. */
  videoCoverFrameUrl?: string;
  /** Legacy ID-only correlation; new native callers must use smartFixEvaluations. */
  smartFixEvaluationIds?: SmartFixEvaluationIdList;
  /** Exact native correlation projection; outcomes remain server-authored. */
  smartFixEvaluations?: SmartFixPublicationCorrelationList;
  /** Server-issued transcode job id (lets the server reconcile the cached result). */
  videoJobId?: string;
  [key: string]: unknown;
}

export interface GenerateCaptionBody {
  restaurantId: number;
  menuItemId?: number;
  imageUrl?: string;
  platform?: string;
  postType?: string;
  [key: string]: unknown;
}

export interface GenerateCaptionResponse {
  captions: Record<string, string>;
  [key: string]: unknown;
}

// ─── Per-platform caption generation (Composer Step 2 — Task #1007) ──────────

/**
 * The social platforms the composer publishes to.
 *
 * `"eatzy"` is always-on (decision #14): callers MUST NOT pass it as the
 * `platform` field in `generateCaptionPerPlatform` — the Eatzy caption is
 * always auto-derived server-side from the Instagram caption with the
 * menu deep-link already injected.  It is included here so downstream
 * code that processes publish results can reference a complete platform set.
 */
export type CaptionPlatform = "instagram" | "facebook" | "tiktok" | "eatzy";

/**
 * A single AI-generated caption variant for one platform.
 */
export interface GenerateCaptionVariant {
  /** Main body text — hashtags already stripped (server appends canonical block). */
  body: string;
  /** Hashtag block to append, normalised by the server's captionNormalizer. */
  hashtags: string[];
  /** Approximate character count including hashtags and the short link. */
  charCount: number;
}

/**
 * Request body for `generateCaptionPerPlatform`.
 *
 * Maps to `POST /api/partner/posts/generate-caption`.
 *
 * Wire contract (what the server actually reads):
 *   - `postType` (required — server returns 400 when absent)
 *   - `itemName`, `itemDescription`, `tags`, `promoDetails`,
 *     `imageUrl`, `targetPlatforms`, `promoLink` (all optional)
 *   - `restaurantId` and `menuItemId` are NOT consumed server-side; the
 *     server derives restaurant context from the authenticated session.
 *
 * The client-side `platform` field is a UX hint used by the client to:
 *   a) populate `targetPlatforms` on the wire when not supplied explicitly, and
 *   b) label the response with the platform that was requested.
 * The `platform` string is stripped from the body before sending so the server
 * never sees an unrecognised field.
 *
 * `eatzy` MUST NOT be passed as `platform` — Eatzy captions are auto-derived
 * server-side from the Instagram caption (decision #14).
 */
export interface GenerateCaptionPerPlatformBody {
  /** Current active editor text. Empty explicitly requests generation from context. */
  existingCaption?: string;
  /** All selected dishes; prices are intentionally not part of caption context. */
  specialsItems?: { name: string; description?: string }[];
  /** Intended publishing instant, ISO 8601 with offset. Restaurant timezone is server-owned. */
  intendedPostAt?: string;
  /**
   * Post type used to select the server-side prompt branch and for caption
   * telemetry.  Required — the server returns 400 when absent.
   * Typical values: `"daily_special"`, `"menu_highlight"`, `"event"`, etc.
   */
  postType: string;
  /**
   * Client-side platform hint.  Maps to `targetPlatforms` on the wire when
   * `targetPlatforms` is not supplied explicitly.  Does NOT appear in the
   * body sent to the server.
   * Eatzy generates its own prose-only caption without social links or hashtags.
   */
  platform: CaptionPlatform;
  /** Optional name of the item being featured in the post. */
  itemName?: string;
  /** Optional description of the item. */
  itemDescription?: string;
  /** Optional tags/labels describing the post content. */
  tags?: string[];
  /**
   * Validated image URL used for vision-based generation (GPT-4o Vision).
   * Omit to fall back to GPT-4o-mini (text-only).
   */
  imageUrl?: string;
  /**
   * Explicit platform target list.  Overrides the `platform` field when
   * provided.  TikTok-specific hook format, IG/FB character limits, emoji
   * policy, and hashtag rules are applied per entry.
   */
  targetPlatforms?: string[];
  /** Optional FB/IG promo link injected above the hashtag block. */
  promoLink?: string;
  /** Optional promotion details (discount type, amount, code). */
  promoDetails?: {
    discountType: "fixed" | "percent" | "free_delivery" | "existing";
    discountAmount?: number;
    code?: string;
  };
}

/**
 * Response from `generateCaptionPerPlatform`.
 *
 * Contains exactly 3 ordered variants (warm, punchy, story) with the
 * Eatzy deep-link already injected server-side.  The `generationId` is
 * opaque and should be forwarded to `POST /api/partner/posts/caption-events`
 * when the partner picks or discards a variant.
 */
export interface GenerateCaptionPerPlatformResponse {
  platform: CaptionPlatform;
  captions: [
    GenerateCaptionVariant,
    GenerateCaptionVariant,
    GenerateCaptionVariant,
  ];
  generationId: string;
  /**
   * Optional human-readable description of the local event that influenced
   * the AI caption (e.g. "Valentine's Day this Saturday"). Present only when
   * the server detected a relevant upcoming event for the restaurant's locale.
   * Consumers should surface this as a dismissible chip so the partner
   * understands why the caption has a particular flavour.
   */
  localEventContext?: string;
}

/**
 * Request body for `polishCaption`.
 *
 * Maps to `POST /api/partner/posts/polish-caption`.
 *
 * "Polish my words" for the Write / Speak caption modes: refines the partner's
 * OWN prose (grammar / flow / brand tone) WITHOUT inventing new facts. The
 * server sources brand voice from the authenticated session, so only the text
 * to refine is sent.  Per-platform hashtags + links are added at publish, so
 * the response is prose only.
 */
export interface PolishCaptionBody {
  /** The partner's own caption text to refine. Required. */
  text: string;
  /** Optional post-type hint (telemetry / prompt tone). Currently unused server-side. */
  postType?: string;
  /** Structured offer values that may remain while menu-item prices are removed. */
  promoDetails?: {
    discountType: "fixed" | "percent" | "free_delivery" | "existing";
    discountAmount?: number;
    code?: string;
  };
}

/**
 * Response from `polishCaption`. A single refined caption body (prose only —
 * no hashtag block, no link line).
 */
export interface PolishCaptionResponse {
  body: string;
}

/**
 * Events emitted by `generateCaptionStreaming`.
 *
 * The `responseHeartbeat` middleware sends whitespace chunks every ~25 s to
 * prevent Cloudflare 524 timeouts on long vision-model calls.  Callers can
 * use `"heartbeat"` events to drive a progress indicator, and `"complete"`
 * when the final JSON payload arrives.
 */
export type CaptionStreamEvent =
  | { type: "heartbeat" }
  | { type: "partial"; text: string }
  | { type: "complete"; data: GenerateCaptionPerPlatformResponse }
  | { type: "error"; message: string };

export interface MenuItem {
  /** Eatzy returns both numeric and canonical I-prefixed item identifiers. */
  id: number | string;
  name: string;
  description?: string | null;
  price?: number | string | null;
  category?: string | null;
  imageUrl?: string | null;
  /** URL of the dish video clip saved to the Eatzy menu item (Task #1935 / #1936). */
  videoUrl?: string | null;
  /** Still-frame thumbnail extracted from the video on upload (Task #1958). */
  videoThumbnailUrl?: string | null;
  eatzyItemId?: string | null;
  /** Eatzy menu id this item belongs to (populated by the partner menu list endpoint). */
  menuId?: number | null;
  /**
   * Eatzy item status. `'active'` — visible on public menu / eligible for
   * posts. `'inactive'` — soft-deactivated ("until I add it back"). Partners
   * can toggle this without deleting and recreating the item.
   */
  status?: string | null;
  [key: string]: unknown;
}

export interface MenuCategory {
  id: number | string;
  name: string;
  items: MenuItem[];
  /** Parent menu is present even when the category has no items. */
  menuId?: number;
  sort?: number;
}

export interface PartnerMenuResponse {
  /** Valid category/menu pairs, including empty categories omitted from lists. */
  creationCategories?: MenuCategory[];
  menus?: MenuCategory[];
  categories?: MenuCategory[];
  [key: string]: unknown;
}

// ─── Phase 2: Menu item CRUD types ───────────────────────────────────────────

/**
 * Detailed item response from
 * `GET /api/partner/restmanager/:restaurantId/menus/:menuId/items/:itemId`.
 *
 * The server strips the Eatzy internal "I" prefix and returns the raw numeric
 * id under `numericId`; the prefixed form (`I<n>`) is available as `id`.
 */
export interface PartnerMenuItemDetail {
  /** Consumer-facing id with the "I" prefix, e.g. `"I565852"`. */
  id: string;
  /** Raw Eatzy numeric id (without prefix). */
  numericId: number;
  name: string;
  description?: string | null;
  price?: number | null;
  categoryId?: string | null;
  menuId?: number | null;
  service?: string[];
  type?: string | null;
  image?: string | null;
  headerImage?: string | null;
  media?: Array<{ url: string; name?: string; [key: string]: unknown }>;
  [key: string]: unknown;
}

/**
 * Request body for creating a new menu item.
 * Maps to `POST /api/partner/restmanager/:restaurantId/menus/:menuId/items`.
 */
export interface PartnerMenuItemCreateBody {
  name: string;
  price: number;
  /** Eatzy category id string. */
  categoryId?: string | undefined;
  description?: string | undefined;
  service?: string[];
  [key: string]: unknown;
}

/**
 * Request body for updating an existing menu item.
 * Maps to `POST /api/partner/restmanager/:restaurantId/menus/:menuId/items/:itemId`.
 *
 * All fields are optional; only supplied fields are forwarded to the Eatzy API.
 */
export interface PartnerMenuItemUpdateBody {
  name?: string;
  description?: string | undefined;
  price?: number;
  categoryId?: string | undefined;
  /**
   * Target menu id when moving an item to a different menu.
   * The caller should also pass the new menu id as the `menuId` URL path
   * parameter to `updateItem` — this field is forwarded in the request body
   * for Eatzy API implementations that accept it there as well.
   */
  menuId?: number | string;
  /**
   * The menu the item currently lives in, before the move.
   * Required when moving an item to a different menu (`menuId` in the URL
   * path differs from the item's current menu).  The server proxy uses this
   * to fetch the existing item state, create it in the target menu, then
   * delete it from the source menu — because the Eatzy API does not support
   * native cross-menu moves via a single update call.
   */
  sourceMenuId?: number | string;
  service?: string[];
  sort?: number;
  type?: string;
  /** URL of a dish video clip to persist against the menu item. */
  videoUrl?: string;
  [key: string]: unknown;
}

// ─── Phase 2: Account deletion response types ─────────────────────────────────

/**
 * Response from `POST /api/account/delete`.
 * Returned after the account is successfully scheduled for deletion.
 */
export interface AccountDeletionResponse {
  success: boolean;
  message: string;
  /** ISO string of when the hard-delete will occur (30 days from now). */
  deletionScheduledAt?: string | Date;
  gracePeriodDays?: number;
  /** True when the account was already scheduled before this call (idempotent). */
  alreadyScheduled?: boolean;
}

/**
 * Response from `POST /api/account/delete/cancel`.
 */
export interface AccountCancelDeletionResponse {
  success: boolean;
  message: string;
}

export interface PartnerNotification {
  id: number;
  type: string;
  title?: string | null;
  message?: string | null;
  isRead?: boolean;
  isDismissed?: boolean;
  createdAt?: string;
  [key: string]: unknown;
}

export type MetaRecoveryReason =
  | "cancelled"
  | "session_expired"
  | "attempt_expired"
  | "missing_grants"
  | "no_pages"
  | "account_ineligible"
  | "token_expired"
  | "provider_unavailable"
  | "unknown";

export type SocialAuthorizationProvider =
  | "instagram_login"
  | "facebook_login"
  | null;
export type MobileReconnectPlatform = "instagram" | "facebook" | "tiktok";
export type InstagramOAuthProvider = Exclude<SocialAuthorizationProvider, null>;
/**
 * Fourth-argument reconnect options. This keeps the OAuth provider separate
 * from the social destination being repaired; `manage` intentionally carries
 * neither, because account management must not guess a provider from unknown
 * connection state.
 */
export interface MobileReconnectUrlOptions {
  restaurantId?: string;
  requestedPlatform?: MobileReconnectPlatform;
  provider?: InstagramOAuthProvider;
  manage?: boolean;
}
export type SocialProviderAccountType = "business" | "creator";
export interface SocialProviderCapability {
  supported: boolean;
  reason?: string;
}
export type SocialProviderCapabilities = Partial<
  Record<
    | "publish"
    | "carousel_publish"
    | "reel_publish"
    | "story_publish"
    | "media_status"
    | "comment"
    | "insights"
    | "engagement",
    SocialProviderCapability
  >
>;
/** Availability of OAuth products exposed by the social OAuth status route. */
export interface SocialOAuthStatus {
  configured: boolean;
  redirectUri: string;
  instagramDirectEnabled?: boolean;
  instagramDirectProductionEnabled?: boolean;
  instagramDirectConfigured?: boolean;
  tiktokEnabled?: boolean;
  tiktokConfigured?: boolean;
  tiktokRedirectUri?: string;
  tiktokSandbox?: boolean;
}

/**
 * Social platform connection status row returned by
 * `GET /api/partner/social/connections`.
 *
 * Tokens are held server-side (decision #17). This type covers both the
 * full-detail view used by the Connected Accounts screen and the slim
 * view used by the onboarding Connect step.
 */
export interface SocialConnection {
  id: string;
  platform: string;
  platformAccountId: string | null;
  platformAccountName: string | null;
  platformAccountHandle: string | null;
  isActive: boolean;
  lastError: string | null;
  accessTokenExpiresAt: string | null;
  scopes: string[] | null;
  scopeStatus?:
    | "current"
    | "reconnect_required"
    | "legacy_unknown"
    | "not_applicable";
  requiredScopes?: string[];
  grantedScopes?: string[] | null;
  missingScopes?: string[];
  publishingRequiredScopes?: string[];
  missingPublishingScopes?: string[];
  optionalScopes?: string[];
  missingOptionalScopes?: string[];
  reconnectRequired?: boolean;
  isLegacyConnection?: boolean;
  authorizationProvider?: SocialAuthorizationProvider | null;
  providerAccountType?: SocialProviderAccountType | null;
  capabilities?: SocialProviderCapabilities | null;
  /** @deprecated compatibility alias for pre-capability dashboard responses. */
  providerCapabilities?: SocialProviderCapabilities | null;
  recoveryReason?: MetaRecoveryReason | null;
  connectionProvenance?: MetaConnectionProvenance | null;
  reconnectPlatform?: "facebook" | "instagram" | "tiktok" | null;
}

/** Social health row returned by the mobile dashboard endpoint. */
export type MobileDashboardSocialPlatform = SocialPlatformHealth;
export type QuickPostObjective = "reach" | "conversion";

/**
 * The server-authored, post-level objective result. Platform legs are
 * evaluated together on the server so clients do not aggregate metrics or
 * infer availability or attribution.
 */
export interface QuickPostPerformanceObjectiveExplanation {
  postId: string;
  objective: QuickPostObjective;
  objectiveInferred: boolean;
  status:
    | "available"
    | "delayed"
    | "unsupported"
    | "not_yet_available"
    | "unavailable";
  value: number | null;
  attributionDirect: boolean;
  disclosure: string;
  minimumPatternSample: number;
}

/**
 * Purpose evaluation returned by the existing QuickPost performance report.
 * Values and availability states are server-authored; clients must not infer
 * missing metrics or attribution.
 */
export interface QuickPostPerformancePost {
  postId: string;
  platform: string;
  marketingObjective: QuickPostObjective | null;
  objectiveAvailability: "available" | "inferred" | "unavailable";
  objectiveConfidence: "direct" | "assisted" | "inferred" | "none";
  evaluationStatus:
    | "evaluated"
    | "delayed"
    | "measurement_unavailable"
    | "objective_unavailable";
  primaryMeasure: {
    metric: "reach" | "attributed_conversion_rate";
    value: number | null;
    numerator: string;
    denominator: string | null;
    denominatorValue?: number | null;
    availability: string;
    attributionConfidence: "direct" | "inferred" | "none";
  } | null;
  attributionDisclosure: string;
  objectiveExplanation: QuickPostPerformanceObjectiveExplanation | null;
}

export interface QuickPostPerformanceReport {
  periodDays: 7 | 30 | 90;
  posts: QuickPostPerformancePost[];
  objectivePerformance?: {
    insights?: {
      minimumPatternSample?: number;
    };
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Module interfaces
// ─────────────────────────────────────────────────────────────────────────────

export interface AuthModule {
  signIn(email: string, password: string): Promise<AuthSignInResponse>;
  signUp(
    email: string,
    password: string,
    name: string,
    opts?: { inviteToken?: string }
  ): Promise<AuthSignInResponse>;
  /**
   * Exchange a native Apple Sign-In identity token for a Kudoz session.
   * Maps to `POST /api/auth/apple`.
   */
  signInWithApple(identityToken: string): Promise<AuthSignInResponse>;
  /**
   * Exchange a native Google Sign-In ID token for a Kudoz session.
   * Maps to `POST /api/auth/google`.
   */
  signInWithGoogle(idToken: string): Promise<AuthSignInResponse>;
  signOut(): Promise<void>;
  /** Returns true if a fresh Kudoz JWT was stored, false if unrecoverable. */
  refreshKudozToken(): Promise<boolean>;
  /** Returns true if a fresh Eatzy access token was stored, false otherwise. */
  refreshEatzyToken(): Promise<boolean>;
  /**
   * Task #2776 — mint a fresh Eatzy access token from the partner's email +
   * password via `POST /api/partner/restmanager/auth` and persist it. Used to
   * recover when sign-in returned `eatzyTokenMintFailed` (the device has no
   * Eatzy token and therefore no refresh token to renew). Never throws;
   * returns true when a token was stored, false otherwise.
   */
  mintEatzyToken(email: string, password: string): Promise<boolean>;
  /**
   * Task #3535 — mint a fresh Eatzy access token for an SSO partner (Google /
   * Apple / Facebook) who has no stored password. Exchanges the partner's
   * Auth Hub session (sent automatically as the `Authorization` bearer) for an
   * Eatzy token via `POST /api/partner/restmanager/auth-sso` and persists it.
   * Never throws; returns `true` when a token was stored, `false` otherwise
   * (including when the Hub exchange endpoint is not deployed yet).
   */
  mintEatzyTokenFromSession(): Promise<boolean>;
  session(): Promise<AuthSessionResponse | null>;
  /** Returns the role payload for the authenticated user. */
  role(): Promise<Record<string, unknown>>;
}

export interface UserModule {
  me(): Promise<ApiUser>;
}

export interface PartnerPostsModule {
  list(
    params?: Record<string, string | number | boolean>
  ): Promise<PartnerPostsListResponse>;
  listPending(): Promise<PartnerPost[]>;
  listScheduled(): Promise<PartnerPost[]>;
  create(body: PartnerPostCreateBody): Promise<PartnerPostCreateResponse>;
  update(
    postId: number | string,
    body: Record<string, unknown>
  ): Promise<PartnerPost>;
  delete(
    postId: number | string
  ): Promise<{ success: boolean; wasScheduled: boolean }>;
  /**
   * Approve a `pending_approval` partner post. Caller must hold the
   * `approve_others` capability (enforced server-side).
   *
   * Maps to `POST /api/partner/posts/:postId/approve`.
   */
  approve(postId: number | string): Promise<PartnerPostApproveResponse>;
  /**
   * Reject a `pending_approval` partner post. The `note` is required and
   * surfaces in the staff member's inbox so they can revise the post.
   * Caller must hold BOTH `approve_others` and `reject_others`.
   *
   * Maps to `POST /api/partner/posts/:postId/reject`.
   */
  reject(
    postId: number | string,
    note: string
  ): Promise<PartnerPostRejectResponse>;
  /**
   * Triggers a server-side retry of any cross-post legs in `failed` state for
   * this post. Returns immediately with queue metadata — the actual retry
   * happens asynchronously. Poll `getSocialStatus(postId)` to observe results.
   */
  retrySocial(postId: number | string): Promise<Record<string, unknown>>;
  /**
   * Retry delivery of a previously intended link comment only. This never
   * republishes the social post or changes its Page/account identity.
   * `platform` defaults to Facebook server-side.
   */
  retryLinkDelivery(
    postId: number | string,
    platform?: "facebook" | "instagram",
    operationId?: string
  ): Promise<{ queued: boolean; linkDelivery: LinkDeliveryStatus }>;
  /**
   * Fetch the current per-platform cross-post status rows for a partner post.
   * Returns an empty array when the cross-post has not yet been enqueued.
   *
   * Maps to `GET /api/partner/social/posts/:partnerPostId`.
   */
  getSocialStatus(postId: number | string): Promise<SocialPostStatus[]>;
  /**
   * Uploads one or more media files to object storage and returns the
   * server-hosted URLs. Required before `create()` for any media captured
   * on-device, since cross-post legs (FB/IG/TikTok) cannot fetch
   * `file://` URIs from the partner's phone.
   *
   * Maps to `POST /api/media/upload` (multipart/form-data, `files[]` field).
   *
   * Pass `options.onProgress` to receive 0–100 upload-progress updates.
   * The callback fires on the XHR upload-progress event; when the request
   * body is not computable (very rare for FormData) it is not invoked and
   * callers should fall back to an indeterminate indicator.
   *
   * Pass `options.signal` to abort the upload (e.g. when the user taps Cancel).
   * The promise rejects with an `AbortError` if the signal is triggered.
   *
   * Pass `opts.onProgress` to receive integer 0–100 upload-progress updates.
   * When supplied the request is routed through XMLHttpRequest (same as the
   * video-upload path) so the runtime can report byte-level progress.
   * Callers that omit `onProgress` continue to use the simpler fetch path.
   */
  uploadMedia(
    formData: FormData,
    options?: {
      onProgress?: (
        percent: number,
        bytesPerSecond: number,
        totalExpected: number
      ) => void;
      /**
       * Called at the start of each retry attempt (before the back-off delay),
       * with the 1-based retry attempt number. Callers can use this to reset
       * their progress UI to an indeterminate state during the back-off window
       * so the partner sees a clear signal that the upload is being retried
       * rather than a frozen progress bar.
       */
      onRetry?: (attempt: number) => void;
      signal?: AbortSignal;
      /**
       * Task #2706 — optional flow correlation id sent as `X-Request-Id`. Pass
       * the same id to the following `linkPhoto` call so the upload and the
       * save share one requestId across the server log lines.
       */
      requestId?: string;
    }
  ): Promise<MediaUploadResponse>;
  /** @deprecated Use `generateCaptionPerPlatform` for the Composer Step 2 flow. */
  generateCaption(body: GenerateCaptionBody): Promise<GenerateCaptionResponse>;
  schedule(postId: number, scheduledAt: string | null): Promise<PartnerPost>;

  /**
   * Generate exactly 3 caption variants (warm / punchy / story) for one platform.
   *
   * Maps to `POST /api/partner/posts/generate-caption` with `platform` set.
   * The server applies per-platform prompt tuning (TikTok hook format,
   * IG/FB character limits, emoji policy, canonical hashtag block) and
   * injects the Eatzy deep-link before returning.
   *
   * Use `generateCaptionStreaming` when a vision call is expected (imageUrl
   * provided) so the client can drive a heartbeat progress indicator.
   */
  generateCaptionPerPlatform(
    body: GenerateCaptionPerPlatformBody
  ): Promise<GenerateCaptionPerPlatformResponse>;

  /**
   * Refine the partner's OWN caption prose ("Polish my words") for the
   * Write / Speak caption modes.
   *
   * Maps to `POST /api/partner/posts/polish-caption`. Unlike
   * `generateCaptionPerPlatform` this invents nothing — it fixes grammar,
   * flow, and brand tone while preserving the partner's meaning and facts,
   * returning a single prose caption (no hashtag block / link line, which are
   * added per-platform at publish).
   */
  polishCaption(body: PolishCaptionBody): Promise<PolishCaptionResponse>;

  /**
   * Streaming variant of `generateCaptionPerPlatform`.
   *
   * The `responseHeartbeat` middleware on the server sends whitespace chunks
   * every ~25 s during long vision-model calls to prevent Cloudflare 524
   * timeouts.  This method reads the response body as a stream and fires
   * `onEvent` for each chunk type:
   *
   * - `"heartbeat"` — whitespace-only chunk; use to pulse a spinner.
   * - `"partial"` — non-whitespace accumulated text (not yet valid JSON).
   * - `"complete"` — final parsed payload; same shape as `generateCaptionPerPlatform`.
   * - `"error"` — stream aborted or JSON parse failure.
   *
   * Pass an `AbortSignal` to cancel the in-flight request (e.g. when the
   * user navigates away from the composer).
   *
   * Returns the same resolved payload as `"complete"` for `async/await`
   * callers that don't need intermediate events.
   */
  generateCaptionStreaming(
    body: GenerateCaptionPerPlatformBody,
    onEvent: (event: CaptionStreamEvent) => void,
    signal?: AbortSignal
  ): Promise<GenerateCaptionPerPlatformResponse>;

  /**
   * Record the partner's decision on a generated caption variant.
   * Must be called after the partner picks, edits, or dismisses variants.
   *
   * Maps to `POST /api/partner/posts/caption-events`.
   *
   * Wire contract (server-validated):
   *   - `generationId` (string, 8–64 chars) — opaque ID from the generate response
   *   - `variantIndex` (int 0–10) — index of the variant the partner picked
   *   - `postType` (string, 1–64 chars) — echoes the postType from generation
   *   - `eventKind` ("chosen") — only `"chosen"` is accepted today
   *
   * @param generationId — opaque ID from `GenerateCaptionPerPlatformResponse`.
   * @param postType — the same `postType` that was passed to `generateCaptionPerPlatform`.
   * @param variantIndex — 0 / 1 / 2 (index into the `captions` tuple).
   * @param signal — optional AbortSignal to cancel an in-flight request.
   */
  recordCaptionEvent(
    generationId: string,
    postType: string,
    variantIndex: number,
    signal?: AbortSignal
  ): Promise<void>;
}

/**
 * Task #3617 — A background-music track returned by
 * `GET /api/partner/audio/tracks`. Curated library rows have
 * `restaurantId === null`; the partner's own uploads carry their restaurant id
 * (and `licenseSource === "partner_upload"`, which makes them deletable).
 * Mirrors the web AudioStep's `AudioTrack` shape exactly.
 */
export interface PartnerAudioTrack {
  id: string;
  title: string;
  artist: string | null;
  moods: string[];
  durationSec: number;
  fileUrl: string;
  previewUrl: string | null;
  licenseSource: "pixabay" | "fma" | "eatzy_owned" | "partner_upload";
  licenseAttribution: string | null;
  restaurantId: string | null;
}

/** Wire response for `GET /api/partner/audio/tracks`. */
export interface PartnerAudioTracksResponse {
  tracks: PartnerAudioTrack[];
}

/** A mood option returned by `GET /api/partner/audio/moods`. */
export interface PartnerAudioMood {
  slug: string;
  label: string;
}

/** Wire response for `GET /api/partner/audio/moods`. */
export interface PartnerAudioMoodsResponse {
  moods: PartnerAudioMood[];
}

/**
 * Task #3617 — Partner background-music library (curated + own uploads).
 * Backs the mobile audio picker at parity with the web AudioStep.
 */
export interface PartnerAudioModule {
  /**
   * List curated tracks merged with the partner's own uploads, optionally
   * filtered by `mood` and a free-text `q` (title / artist).
   * Maps to `GET /api/partner/audio/tracks`.
   */
  list(filter?: {
    mood?: string;
    q?: string;
  }): Promise<PartnerAudioTracksResponse>;
  /**
   * Fetch the dynamic mood list from the server (admin-managed).
   * Maps to `GET /api/partner/audio/moods`.
   */
  moods(): Promise<PartnerAudioMoodsResponse>;
  /**
   * Upload a partner-owned track. `formData` must include the `audio` file part
   * and may include `title`, `artist`, and `moods` string parts.
   * Maps to `POST /api/partner/audio/upload` (multipart/form-data).
   */
  upload(formData: FormData): Promise<{ track: PartnerAudioTrack }>;
  /**
   * Delete one of the partner's own uploaded tracks (curated rows reject).
   * Maps to `DELETE /api/partner/audio/tracks/:id`.
   */
  delete(id: string): Promise<void>;
}

export interface PartnerMenuModule {
  list(restaurantProfile: string): Promise<PartnerMenuResponse>;

  // ── Phase 2: item CRUD ────────────────────────────────────────────────────

  /**
   * Fetch a single menu item's full details.
   *
   * Maps to `GET /api/partner/restmanager/:restaurantId/menus/:menuId/items/:itemId`.
   *
   * @param restaurantId  Eatzy restaurant id (numeric or UUID string).
   * @param menuId        Eatzy menu id.
   * @param itemId        Item id. Accepts either the prefixed form (`I565852`)
   *                      or the bare numeric string (`565852`) — the server
   *                      strips the prefix before forwarding to Eatzy.
   */
  getItem(
    restaurantId: number | string,
    menuId: number | string,
    itemId: string
  ): Promise<PartnerMenuItemDetail>;

  /**
   * Create a new menu item in the given menu.
   *
   * Maps to `POST /api/partner/restmanager/:restaurantId/menus/:menuId/items`.
   */
  createItem(
    restaurantId: number | string,
    menuId: number | string,
    body: PartnerMenuItemCreateBody
  ): Promise<PartnerMenuItemDetail>;

  /**
   * Update an existing menu item.
   *
   * Maps to `POST /api/partner/restmanager/:restaurantId/menus/:menuId/items/:itemId`.
   * (The server uses POST, not PATCH, for this mutation.)
   */
  updateItem(
    restaurantId: number | string,
    menuId: number | string,
    itemId: string,
    body: PartnerMenuItemUpdateBody
  ): Promise<PartnerMenuItemDetail>;

  /**
   * Delete a menu item permanently.
   *
   * Maps to `DELETE /api/partner/restmanager/:restaurantId/menus/:menuId/items/:itemId`.
   */
  deleteItem(
    restaurantId: number | string,
    menuId: number | string,
    itemId: string
  ): Promise<void>;

  /**
   * Deactivate a menu item ("until I add it back" — soft delete on Eatzy).
   *
   * Maps to `DELETE /api/partner/restmanager/:restaurantId/menus/:menuId/items/:itemId`.
   *
   * The item is NOT permanently removed. It stays in the list with
   * `status: 'inactive'` and can be re-activated via `restoreItem`.
   */
  deactivateItem(
    restaurantId: number | string,
    menuId: number | string,
    itemId: string
  ): Promise<void>;

  /**
   * Re-activate a previously deactivated menu item.
   *
   * Maps to `PUT /api/partner/restmanager/:restaurantId/menus/:menuId/items/:itemId/restore`.
   */
  restoreItem(
    restaurantId: number | string,
    menuId: number | string,
    itemId: string
  ): Promise<void>;

  /**
   * Persist a dish video clip URL against the Eatzy menu item.
   *
   * Maps to `POST /api/partner/restmanager/:restaurantId/menus/:menuId/items/:itemId/link-video`.
   *
   * The `videoUrl` must be a trusted CDN / object-storage URL (validated
   * server-side). On success the Eatzy menu item record is updated and the
   * video survives app reloads.
   */
  linkVideo(
    restaurantId: number | string,
    menuId: number | string,
    itemId: string,
    videoUrl: string,
    /**
     * Optional generated still-frame thumbnail URL. When provided, the server
     * persists it to `dish_video_thumbnails` so the preview frame is available
     * on fresh app loads (no local cache required).
     */
    videoThumbnailUrl?: string
  ): Promise<{ success: boolean }>;

  /**
   * Remove a dish video clip from a menu item.
   *
   * Maps to `DELETE /api/partner/restmanager/:restaurantId/menus/:menuId/items/:itemId/link-video`.
   *
   * Clears the `video_url` field on the Eatzy item and removes the locally-
   * stored thumbnail row so the item shows as having no video after a refetch.
   */
  unlinkVideo(
    restaurantId: number | string,
    menuId: number | string,
    itemId: string
  ): Promise<{ success: boolean }>;

  /**
   * Persist a dish photo URL so it survives app restarts.
   *
   * Maps to `POST /api/partner/restmanager/:restaurantId/menus/:menuId/items/:itemId/link-photo`.
   *
   * The `photoUrl` must be a trusted CDN / object-storage URL (validated
   * server-side). On success the stored URL is merged into `imageUrl` on
   * every subsequent menu fetch (Task #1960).
   */
  linkPhoto(
    restaurantId: number | string,
    menuId: number | string,
    itemId: string,
    photoUrl: string,
    /**
     * Task #2706 — optional flow correlation id. Pass the SAME id used for the
     * preceding media upload so the server's `[UploadCDN]` and
     * `[Partner] link-photo.recv` log lines share one `requestId`, making the
     * upload→save pair joinable when diagnosing a rejected photo host.
     */
    requestId?: string
  ): Promise<{ success: boolean }>;

  /**
   * Remove a dish photo from a menu item.
   *
   * Maps to `DELETE /api/partner/restmanager/:restaurantId/menus/:menuId/items/:itemId/link-photo`.
   *
   * Clears all image media on the Eatzy item (dish videos are preserved), so
   * the item shows the placeholder after a refetch.
   */
  unlinkPhoto(
    restaurantId: number | string,
    menuId: number | string,
    itemId: string
  ): Promise<{ success: boolean }>;
}

export interface PartnerRestaurantsModule {
  list(): Promise<PartnerRestaurant[]>;
}

export interface PartnerNotificationsModule {
  list(): Promise<PartnerNotification[]>;
  markRead(id: number): Promise<void>;
  markAllRead(): Promise<void>;
  dismiss(id: number): Promise<void>;
}

export interface PartnerDashboardModule {
  stats(): Promise<PartnerDashboardStats>;
}

export interface PartnerSocialModule {
  /**
   * Start an OAuth flow for a specific restaurant and platform.
   *
   * Maps to `GET /api/partner/social/oauth/:platform/start`.
   */
  startOAuth(
    platform: "instagram" | "facebook",
    restaurantId: number | string,
    returnTo?: string
  ): Promise<{ url: string }>;
  /** Fetch OAuth feature availability for the selected restaurant. */
  getOAuthStatus(restaurantId: number | string): Promise<{
    configured: boolean;
    redirectUri: string;
    tiktokEnabled: boolean;
    tiktokConfigured: boolean;
    tiktokSandbox: boolean;
    tiktokRedirectUri: string;
    instagramDirectEnabled: boolean;
    instagramDirectConfigured: boolean;
    instagramDirectRedirectUri: string;
  }>;
  /**
   * Fetch the social platform connection statuses for a restaurant.
   *
   * Maps to `GET /api/partner/social/connections?restaurantId=<id>`.
   *
   * Returns one entry per known platform. Platforms with no token row
   * are absent from the array — callers should treat a missing entry as
   * `disconnected`.
   */
  getConnections(restaurantId: number | string): Promise<SocialConnection[]>;
  /**
   * Fetches the existing trustworthy performance report. Consumers must retain
   * its explicit availability and attribution states.
   */
  getPerformance(
    restaurantId: number | string,
    period?: 7 | 30 | 90
  ): Promise<QuickPostPerformanceReport>;
}

export interface AccountDeletionStatus {
  status: "active" | "scheduled" | "deleted";
  scheduledAt: string | null;
  requestedAt: string | null;
  gracePeriodDays: number;
}

export interface AccountModule {
  /**
   * @deprecated Use `requestDeletion(email)` for the Apple 5.1.1(v) flow.
   *
   * Kept for backward compatibility with any existing callers. Internally
   * delegates to `requestDeletion`.
   */
  delete(
    email: string
  ): Promise<{ deletionScheduledAt: string; gracePeriodDays: number }>;

  /**
   * Schedule the authenticated user's account for permanent deletion.
   *
   * The caller must pass the user's own email address as a confirmation token
   * (Apple App Store Rule 5.1.1(v) requirement). The server cross-checks it
   * against the stored account email and rejects mismatches with 400.
   *
   * The deletion is soft: the account enters a 30-day grace period during
   * which `cancelDeletion()` can reverse it. After the grace period the
   * account is permanently removed.
   *
   * Maps to `POST /api/account/delete`.
   */
  requestDeletion(email: string): Promise<AccountDeletionResponse>;

  /**
   * Cancel a previously scheduled account deletion.
   *
   * Only callable within the 30-day grace window. The server returns 400 if
   * no deletion is pending, and 410 if the window has already lapsed.
   *
   * Maps to `POST /api/account/delete/cancel`.
   */
  cancelDeletion(): Promise<AccountCancelDeletionResponse>;

  /** Alias for cancelDeletion() used by the mobile deletion screen. */
  cancelDelete(): Promise<void>;

  getDeletionStatus(): Promise<AccountDeletionStatus>;
}

// ─── Phase 2: Media upload module ─────────────────────────────────────────────

/**
 * Typed module for uploading photo and video media to object storage.
 *
 * Phase 2 addition (Task #1020). Wraps the multipart
 * `POST /api/media/upload` endpoint so callers never have to build
 * FormData manually.
 */
/**
 * Result of a batch upload operation.
 *
 * On partial failure the `failed` array is non-empty and every entry
 * carries the original zero-based `index`, the `name` used for that
 * file, and the human-readable `error` string from the server (or the
 * caught exception message). The native compose step can iterate
 * `failed` to surface a per-image retry affordance (decision #22).
 */
export interface MediaUploadBatchResult {
  succeeded: Array<{
    url: string;
    type: "photo" | "video";
    originalName: string;
    index: number;
  }>;
  failed: Array<{
    /** Zero-based position in the original `files` array. */
    index: number;
    /** Filename that was attempted. */
    name: string;
    /** Human-readable reason the upload failed. */
    error: string;
  }>;
}

/**
 * Per-cell upload state for the native carousel composer grid.
 *
 * The grid renders one `UploadCellState` entry per photo (keyed by the
 * zero-based index in the original `files` array passed to `uploadBatch`).
 *
 * State machine:
 *   idle → uploading → success
 *                   ↘ failed → uploading  (retry path)
 *
 * `progress` is only meaningful in the `uploading` state.  The native grid
 * cell renders it as a bottom fill-bar (0 → 1).  For the initial sequential
 * implementation the value jumps from 0 to 1 when the individual file
 * completes; a future XHR-progress extension can emit intermediate values.
 *
 * `error` carries the human-readable message surfaced in the failed cell
 * tooltip / retry sheet — always the same string as the matching entry in
 * `MediaUploadBatchResult.failed[].error`.
 *
 * Decision #22: no silent auto-retry.  The native UI shows the error and a
 * manual Retry affordance.  Tapping Retry calls `MediaModule.upload()` for
 * just that file and splices the result back into the succeeded set so the
 * composer can proceed once all cells have resolved.
 */
export type UploadCellStatus = "idle" | "uploading" | "success" | "failed";

export interface UploadCellState {
  /** Zero-based position matching the original `files` array index. */
  index: number;
  status: UploadCellStatus;
  /**
   * Upload fill progress in [0, 1].  Only set when `status === "uploading"`.
   * Use to drive the bottom fill-bar on the grid cell.
   */
  progress: number;
  /**
   * Human-readable error string.  Only set when `status === "failed"`.
   * Mirrors `MediaUploadBatchResult.failed[].error`.
   */
  error?: string;
  /**
   * Server-hosted URL.  Only set when `status === "success"`.
   * Mirrors the matching entry in `MediaUploadBatchResult.succeeded[].url`.
   */
  url?: string;
}

export interface MediaModule {
  /**
   * Upload a single photo or video file to object storage.
   *
   * Builds a `multipart/form-data` request with the file attached under the
   * `files` field, then returns the server-hosted URL for use in
   * `PartnerPostCreateBody.media[].url` or `MobilePublishBody.mediaUrls`.
   *
   * Maps to `POST /api/media/upload`.
   *
   * @param file  A `File` or `Blob` instance (browser / RN).
   * @param type  `"photo"` or `"video"` — written into the FormData as a
   *              `type` hint so the server can apply the right processing
   *              pipeline.
   * @param name  Optional filename. Defaults to `"upload"`.
   */
  upload(
    file: File | Blob,
    type: "photo" | "video",
    name?: string
  ): Promise<MediaUploadResponse>;

  /**
   * Upload multiple photo or video files in sequence, reporting per-file
   * progress via `onProgress`.
   *
   * Each file is uploaded individually using the same `POST /api/media/upload`
   * endpoint as `upload()`. Files are processed one at a time so the caller
   * receives deterministic "X of Y uploaded" progress suitable for the
   * native compose-step progress indicator.
   *
   * On partial failure the method does **not** throw — it collects each
   * error and continues uploading remaining files. The return value always
   * contains both `succeeded` and `failed` buckets so the native UI can
   * surface a per-image retry affordance (decision #22 — per-platform
   * failure UX: show error and manual retry per failed item).
   *
   * Up to 10 files are accepted per call, matching the IG-style carousel
   * limit (decision #18).
   *
   * @param files       Array of `{ file, name? }` descriptors. Max 10.
   * @param type        `"photo"` or `"video"` — applied to every file in
   *                    the batch.
   * @param onProgress  Optional callback invoked after each file attempt
   *                    (success or failure) with `(completed, total)`.
   *                    `completed` is 1-based (first call is `(1, total)`).
   */
  uploadBatch(
    files: Array<{ file: File | Blob; name?: string }>,
    type: "photo" | "video",
    onProgress?: (completed: number, total: number) => void
  ): Promise<MediaUploadBatchResult>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Video upload + transcoding types (Task #1008)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Trim window + cover-frame selection emitted by the native trim UI.
 * All values are in milliseconds relative to the start of the uploaded
 * master clip.
 */
export type VideoTrimSpec = import("@kudoz/shared/types/videoTrimSpec").VideoTrimSpec;

export interface VideoPlatformEncoding {
  platform: "instagram" | "facebook" | "tiktok" | "eatzy";
  url: string;
  width: number;
  height: number;
  durationSec: number;
  container: string;
}

export interface VideoTranscodeResult {
  jobId: string;
  masterUrl: string;
  coverFrameUrl: string;
  encodings: VideoPlatformEncoding[];
  completedAt: string;
}

/**
 * Snapshot returned by `PartnerVideoModule.getStatus` while the transcode
 * job is still running. `completedEncodings` / `totalEncodings` come from
 * the server-side ffmpeg pipeline (Task #1085) and let the mobile overlay
 * render "X of Y platforms ready" with a determinate progress bar instead
 * of the indeterminate sweep that used to cover the transcoding stage.
 *
 * `totalEncodings` is `null` for very old job rows that pre-date the
 * progress columns; clients should fall back to an indeterminate UI in
 * that case.
 *
 * `progressStep` (Task #1120) is the coarse pipeline stage currently
 * running — e.g. `"downloading"`, `"trimming"`, `"encoding_instagram"`,
 * `"uploading"`. `null` until the first DB write lands (very brief window
 * after the job transitions to `processing`). Clients should show a
 * generic label when `null`.
 */
export interface VideoTranscodeProgress {
  completedEncodings: number;
  totalEncodings: number | null;
  progressStep: string | null;
}

/**
 * Discriminated union returned by `PartnerVideoModule.getStatus`.
 *
 *   - `{ kind: "done", result }`     — job finished; `result` is the
 *     full `VideoTranscodeResult` ready for downstream cross-posting.
 *   - `{ kind: "progress", progress }` — job is still running; surface
 *     `progress` to the UI so it can show a determinate bar.
 *   - `{ kind: "failed", errorMessage, errorCode }` — the server worker
 *     permanently failed the job; callers should surface `errorMessage`
 *     to the user and stop polling immediately (Decision #22: no silent
 *     auto-retry, no deferred share-later queue).
 *   - `null` — job is unknown or has expired from the server cache; the
 *     caller should treat this as a transient miss and keep polling
 *     (the existing contract).
 */
export type VideoTranscodeStatus =
  | { kind: "done"; result: VideoTranscodeResult }
  | { kind: "progress"; progress: VideoTranscodeProgress }
  | { kind: "failed"; errorMessage: string; errorCode: string };

export interface VideoUploadResponse {
  masterUrl: string;
}

/**
 * Response from `POST /api/media/upload`. Each entry is one uploaded file
 * with its server-hosted URL — pass these into
 * `PartnerPostCreateBody.media[].url` so cross-post legs can fetch the
 * media over HTTPS.
 */
export interface MediaUploadResponse {
  files: Array<{
    url: string;
    type: "photo" | "video";
    originalName: string;
    index: number;
    /** Still-frame JPEG extracted from the video at upload time (Task #1958). */
    thumbnailUrl?: string;
  }>;
}

export interface VideoTranscodeBody {
  /**
   * Accepts the numeric Eatzy ID or the internal UUID — `resolveRestaurantId`
   * on the server normalises either form before authorising the partner.
   */
  restaurantId: number | string;
  masterUrl: string;
  trimSpec: VideoTrimSpec;
  /**
   * Deeplink URL to encode in the end-card QR and text overlay (Task #4311).
   * When provided, a static 2.5-second end-card frame is appended to the
   * transcoded video for all platforms. Attributed as `placement=video_end_card`.
   * When absent the end-card step is skipped entirely.
   */
  deepLinkUrl?: string;
}

/**
 * Response from `POST /api/partner/video/transcode` (Task #1085).
 *
 * The server kicks the job off asynchronously and returns the `jobId`
 * as soon as the job row exists, so the mobile overlay can start polling
 * `/api/partner/video/:jobId/status` for "X of Y platforms ready"
 * progress instead of blocking on the full encode. Cache hits return
 * `status: "done"` with the inline `result` in one round-trip.
 */
export type VideoTranscodeAck =
  | { jobId: string; status: "processing" }
  | { jobId: string; status: "done"; result: VideoTranscodeResult };

/**
 * Options for `PartnerVideoModule.uploadMaster`.
 */
export interface UploadMasterOptions {
  /**
   * Called repeatedly while the request body is being sent.
   * `percent` is an integer 0–100. The callback is invoked at least once with
   * `100` when the upload finishes. When the request length is not computable
   * (very rare for FormData) the callback is not invoked and consumers should
   * fall back to an indeterminate progress UI.
   *
   * Implementation note: switches the request to `XMLHttpRequest` so the
   * upload progress event is available in both browser and React Native
   * environments. Falls back to `fetch` when no callback is supplied.
   */
  onProgress?: (percent: number) => void;

  /**
   * Aborts the in-flight upload when triggered (Task #1084).
   *
   * On the XHR path the underlying `xhr.abort()` is called and the returned
   * promise rejects with an `ApiError` whose `code` is `"upload_aborted"`
   * (status `0`). On the fetch fallback path the signal is forwarded to
   * `fetch()` and rejection follows the standard `AbortError` shape.
   *
   * Consumers should check `ApiError.code === "upload_aborted"` (or
   * `signal.aborted`) and suppress error UI when the abort was user-initiated.
   */
  signal?: AbortSignal;
}

/**
 * Request body for `POST /api/partner/video/:jobId/reencode`.
 *
 * Used when a restaurant connects a new social account (e.g. Instagram)
 * after a video was already transcoded. The server encodes only the
 * platforms not already present in the existing result, skipping the
 * trim and cover-frame steps since the trimmed master already exists.
 */
export interface VideoReencodeBody {
  /**
   * The restaurant the job belongs to — resolved from partner-auth session
   * headers, same as the transcode endpoint.
   */
  restaurantId: number | string;
  /**
   * Platform(s) to add. Platforms already encoded in the existing job
   * result are silently skipped — the call is idempotent per platform.
   * Valid values: `"instagram"`, `"facebook"`, `"tiktok"`, `"eatzy"`.
   */
  platforms: string[];
}

/**
 * Response from `POST /api/partner/video/:jobId/reencode`.
 *
 * `result` is the **complete** updated `VideoTranscodeResult` with the
 * new encoding(s) already merged into the original `encodings` array.
 * Callers should replace their stored result with this value so they
 * always have the full platform set.
 */
export interface VideoReencodeResponse {
  jobId: string;
  result: VideoTranscodeResult;
}

export interface PartnerVideoModule {
  /**
   * Upload a raw master video clip.
   * Returns the object-storage URL to include in the transcode request.
   *
   * Pass `options.onProgress` to receive 0–100 progress updates while the
   * request body is being sent (Task #1056).
   */
  uploadMaster(
    formData: FormData,
    options?: UploadMasterOptions
  ): Promise<VideoUploadResponse>;

  /**
   * Kick off server-side transcoding (trim + per-platform encode + cover
   * frame). Returns as soon as the job row exists — `status: "processing"`
   * means the encode is running and the caller should poll `getStatus`
   * for "X of Y platforms ready" progress (Task #1085). A cache hit
   * returns `status: "done"` with the full inline `result`.
   */
  transcode(body: VideoTranscodeBody): Promise<VideoTranscodeAck>;

  /**
   * Look up a transcode job by `jobId`. While the job is still running
   * returns `{ kind: "progress", progress }` so the caller can render a
   * determinate "X of Y platforms ready" bar; once finished returns
   * `{ kind: "done", result }`. Returns `null` if the job is not found
   * or has expired from the server cache — callers should treat this as
   * a transient miss and keep polling (Task #1085).
   */
  getStatus(jobId: string): Promise<VideoTranscodeStatus | null>;

  /**
   * Add encodings for newly-connected social platforms to an existing
   * completed transcode job (Task #2850).
   *
   * Call this when a restaurant connects a new social account (e.g.
   * Instagram) after a video was already transcoded — the existing job
   * result is missing that platform's encoding. The server encodes only
   * the new platforms against the already-trimmed master, then merges
   * them into the original job result.
   *
   * Platforms already encoded are silently skipped — the call is
   * idempotent for platforms that are already present.
   *
   * Returns the **complete** updated result (original encodings + new
   * encodings merged). Callers should replace their stored result with
   * this value so the full platform set is available for cross-posting.
   *
   * Errors:
   *   - `404 JOB_NOT_FOUND`  — the jobId does not exist.
   *   - `400 JOB_NOT_DONE`   — the job has not completed yet.
   *   - `409 JOB_EXPIRED`    — the job result has expired; re-upload needed.
   *   - `400 INVALID_PLATFORMS` — unknown platform name(s).
   */
  reencodeForPlatforms(
    jobId: string,
    body: VideoReencodeBody
  ): Promise<VideoReencodeResponse>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Short-link types
// ─────────────────────────────────────────────────────────────────────────────

export interface ShortLinkCreateBody {
  /**
   * The long URL to shorten. For menu-item deep-links this is
   * `eatzy.com/r/<eatzyId>?item=<menuItemId>` resolved server-side.
   */
  url: string;
  /** Optional human-readable label stored alongside the link (not public). */
  label?: string;
  /** If supplied, the server will try to use this code (best-effort). */
  suggestedCode?: string;
}

export interface ShortLinkCreateResponse {
  /** The short code segment — e.g. `"c4r"`. */
  code: string;
  /** Fully-qualified short URL — e.g. `"https://eatzy.com/go/c4r"`. */
  shortUrl: string;
  /** The original long URL that the short link resolves to. */
  longUrl: string;
}

export interface ShortLinksModule {
  /**
   * Reserve a new short link for the given long URL.
   * Used by the menu picker to bind the selected dish to the post payload
   * as `{ menuItemId, shortLinkCode, longUrl }`.
   *
   * Maps to `POST /api/links/shorten`.
   */
  create(body: ShortLinkCreateBody): Promise<ShortLinkCreateResponse>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Mobile background upload + publish types (Task #1009)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Platforms the mobile composer can target.
 * `"eatzy"` is always-on (decision #14) and appears only in status responses.
 */
export type MobilePublishPlatform =
  | "instagram"
  | "facebook"
  | "tiktok"
  | "eatzy";

/** Compact social-health portion of GET /api/partner/mobile/dashboard. */
export interface MobileDashboardResponse {
  social: SocialPlatformHealth[];
  [key: string]: unknown;
}

/** Aggregate status reported by the mobile post-status endpoint. */
export type MobilePublishOverallStatus =
  | "live"
  | "publishing"
  | "partial"
  | "failed"
  | "pending"
  | "unknown";

/** Per-platform publish status returned by the Status API. */
export interface MobilePerPlatformStatus {
  platform: MobilePublishPlatform;
  /** `queued | uploading | publishing | live | failed` */
  status: string;
  /** Platform-assigned post id when the leg is live. */
  platformPostId?: string | null;
  platformPostUrl: string | null;
  platformPostIdProvenance?: string | null;
  /** Compatibility alias for clients that use a generic provenance field. */
  provenance?: string | null;
  errorMessage: string | null;
  /** Partner-facing headline ("Instagram connection expired"). Never an error code. */
  plainEnglishError: string | null;
  /** Optional expanded detail sentence to display below the headline. */
  plainEnglishDetail: string | null;
  /** When true, show a Reconnect CTA instead of a plain Retry button. */
  suggestReconnect: boolean;
  recoveryAction?:
    | "reconnect"
    | "retry"
    | "refresh_health"
    | "add_comment_manually"
    | null;
  recoveryReason?: MetaRecoveryReason | null;
  connectionProvenance?: MetaConnectionProvenance | null;
  reconnectPlatform?: "facebook" | "instagram" | "tiktok" | null;
  retryCount: number;
  publishedAt: string | null;
  /**
   * Non-fatal warning attached to an otherwise live leg. For example, the
   * post may be live while its optional first comment could not be added.
   */
  warningCode?: string | null;
  warningMessage?: string | null;
  /**
   * Durable first-comment/destination delivery state. This remains available
   * after reopening the post; do not infer it from a platform leg's `live`
   * status or from a QR overlay.
   */
  linkDelivery?: LinkDeliveryStatus | null;
  /**
   * Engagement counts populated from the platform analytics API.
   * Present when status is `live` and the cache has been populated.
   * `null` means not yet available (first request triggers a background fetch).
   */
  likes: number | null;
  comments: number | null;
  /**
   * Unique accounts reached (Instagram/Facebook) or view count (TikTok).
   * `null` when not yet fetched or the platform does not expose this metric.
   */
  reach: number | null;
}

/** Response from `POST /api/partner/mobile/posts`. */
export interface MobilePublishResponse {
  partnerPostId: string;
  /** Initial statuses — all `"queued"` immediately after the call. */
  platforms: MobilePerPlatformStatus[];
  /**
   * Task #2251 — True when the post was created as a scheduled post
   * (not immediately published). The publish queue skips Stage 3 polling
   * and marks the job as done when this is set.
   */
  scheduled?: boolean;
  /** ISO-8601 UTC timestamp of the scheduled publish time, when `scheduled` is true. */
  scheduledFor?: string;
  /**
   * True when `localScheduledFor` was provided but the resolved UTC time was
   * already in the past (or could not be parsed). The post was published
   * immediately; the client should surface a warning toast.
   */
  scheduledDropped?: boolean;
  /** Why the schedule was dropped: `"past"` (resolved time ≤ 10 min away) or `"nan"` (parse failed). */
  scheduledDropReason?: "past" | "nan";
}

/** Response from `GET /api/partner/mobile/posts/:postId/status`. */
export interface MobilePostStatusResponse {
  partnerPostId: string;
  /** Authorized restaurant scope of this post; optional for older servers. */
  restaurantId?: string;
  /** Server-verified generated-source publication provenance. */
  promotionDetails?: {
    smartFixEdited?: {
      version: 1;
      kind: "background-cleanup";
      verified: true;
      assets: Array<{ assetId: string; mediaIndex: number }>;
    };
    [key: string]: unknown;
  };
  /** `live | publishing | partial | failed | unknown` */
  overallStatus: MobilePublishOverallStatus | string;
  platforms: MobilePerPlatformStatus[];
  /** Original caption / body text. */
  caption?: string;
  /** Per-platform caption overrides (if the partner wrote different copy per platform). */
  instagramCaption?: string | undefined;
  facebookCaption?: string | undefined;
  tiktokCaption?: string | undefined;
  /** First media asset URL (thumbnail / hero). */
  mediaUrl?: string;
  mediaType?: string;
  /**
   * Poster/thumbnail image URL for video posts. Sourced from
   * `dish_video_thumbnails` when the post is tagged to a menu item that has a
   * stored video thumbnail. `undefined` when absent — clients should fall back
   * to the generic video-icon placeholder.
   */
  videoThumbnailUrl?: string;
  /** Tagged menu item ID (one per post, decision #30). */
  menuItemId?: number;
  /** ISO-8601 timestamp of when the post was created. */
  createdAt?: string;
  /**
   * Eatzy short link for this post (e.g. `https://app.eatzy.com/go/abc123`).
   * Created on-demand by the server; `undefined` when the post has no canonical
   * Eatzy page yet (e.g. still publishing).
   */
  shortLink?: string;
}

/**
 * A single post entry returned by the today's feed endpoint.
 * Combines review metadata with per-platform statuses for the Status Dashboard.
 */
export interface MobileTodayPost {
  id: string;
  caption: string;
  instagramCaption?: string;
  facebookCaption?: string;
  tiktokCaption?: string;
  eatzyCaption?: string;
  mediaUrl?: string;
  mediaType?: string;
  postType?: string;
  /**
   * Poster/thumbnail image URL for video posts. Sourced from
   * `dish_video_thumbnails` when the post is tagged to a menu item that has a
   * stored video thumbnail. `undefined` when absent — clients should fall back
   * to the generic video-icon placeholder.
   */
  videoThumbnailUrl?: string;
  menuItemId?: number;
  /** Row status from the reviews table (`published`, `pending_approval`, etc.). */
  status: string;
  createdAt: string;
  /**
   * Derived overall status across all platform legs.
   * `live | publishing | partial | failed | pending | unknown`
   */
  overallStatus: string;
  /** Per-platform statuses. Empty when no mobile platform statuses are recorded. */
  platforms: MobilePerPlatformStatus[];
}

/**
 * Per-day summary entry returned by `GET /api/partner/mobile/posts/range`.
 */
export interface MobilePostRangeDaySummary {
  /** ISO date string (YYYY-MM-DD). */
  date: string;
  /** Total posts on this day. */
  total: number;
  /** Posts where every platform is `live`. */
  live: number;
  /** Posts with `pending_approval` status or at least one `pending` platform. */
  pending: number;
  /** Posts where at least one platform is `failed` or `partial`. */
  failed: number;
  /**
   * Posts with `status = "scheduled"` whose `scheduledFor` date falls on this
   * day.  These are future-dated and have no platform-status rows yet.
   * Callers should render a distinct (blue) dot for days where this is > 0.
   */
  scheduled: number;
}

/**
 * Response from `GET /api/partner/mobile/posts/range`.
 * Drives the History heatmap in the mobile dashboard.
 */
export interface MobilePostRangeResponse {
  from: string;
  to: string;
  restaurantId: string;
  /**
   * Sorted ascending by date. Only days with at least one post are included;
   * callers should treat any date absent from this array as zero posts.
   */
  days: MobilePostRangeDaySummary[];
}

/**
 * A single scheduled post entry returned by
 * `GET /api/partner/mobile/scheduled`.
 */
export interface MobileScheduledPost {
  id: string;
  caption: string;
  instagramCaption?: string;
  facebookCaption?: string;
  tiktokCaption?: string;
  eatzyCaption?: string;
  mediaUrl?: string;
  mediaType?: string;
  postType?: string;
  /**
   * Real decoded video-frame poster (Task #3711). Only set when
   * `mediaType === "video"`; falls back to `dish_video_thumbnails` then
   * `reviews.video_thumbnail_url` server-side. `MediaThumbnail` renders this
   * instead of the generic video-camera icon when present.
   */
  videoThumbnailUrl?: string;
  menuItemId?: number;
  status: string;
  /** ISO-8601 UTC timestamp of when this post is scheduled to publish. */
  scheduledFor: string | null;
  createdAt: string;
  /** Social platforms this post will be sent to (e.g. ["instagram", "facebook"]). */
  platforms: string[];
}

/**
 * Response from `GET /api/partner/mobile/scheduled`.
 */
export interface MobileScheduledFeedResponse {
  restaurantId: string;
  posts: MobileScheduledPost[];
}

/**
 * Response from `GET /api/partner/mobile/posts/today`.
 * Drives the Status Dashboard feed + summary strip.
 */
export interface MobileTodayFeedResponse {
  date: string;
  restaurantId: string;
  posts: MobileTodayPost[];
  summary: {
    /** Posts where every platform is `live`. */
    live: number;
    /** Posts with `pending_approval` status. */
    pending: number;
    /** Posts where at least one platform is `failed`. */
    failed: number;
  };
}

/** Request body for `POST /api/partner/mobile/posts`. */
export interface MobilePublishBody {
  /** Canonical restaurant menu URL frozen into the publish queue entry. */
  link?: string;
  /** Immutable identity proof paired with `link` by Stage 2 clients. */
  menuLinkIdentity: {
    nativeRestaurantId: string;
    canonicalLongUrl: string;
  };
  /** Business outcome intent; independent of postType/content purpose. */
  marketingObjective?: {
    objective: "reach" | "conversion";
    subObjective:
      | "awareness"
      | "engagement"
      | "traffic"
      | "orders"
      | "offer_redemption";
    provenance?: "selected" | "recommended" | undefined;
    version?: 1 | undefined;
  };
  /** Portal recommendation that launched this compose flow, when validated. */
  marketingRecommendationId?: string;
  /** Stable UUID for deduplicating post creation after a lost response. */
  clientPublishId?: string;
  /** ID of a completed `MobileUploadSession`. Mutually exclusive with `mediaUrls`. */
  uploadSessionId?: string;
  /** Pre-resolved media URLs. Mutually exclusive with `uploadSessionId`. */
  mediaUrls?: string[];
  /**
   * Optional branded photo overlay per `mediaUrls` slot. The same configuration
   * is rendered after the final geometry for every photo destination; it is not
   * a per-platform variant. Null means no overlay for that slot.
   */
  photoOverlays?: Array<{
    version?: 1;
    logoUrl: string | null;
    headline: string;
    position: "top-left" | "top-right" | "bottom-left" | "bottom-right";
    size: "small" | "medium" | "large";
    contrast: "shadow" | "dark" | "light";
  } | null>;
  /** Per-platform captions. */
  instagramCaption?: string;
  facebookCaption?: string;
  tiktokCaption?: string;
  eatzyCaption?: string;
  /** Tagged menu item ID (one per post — decision #30). */
  menuItemId?: number;
  /**
   * Task #1978 — Per-slot menu item IDs for carousel posts, parallel to
   * `mediaUrls`. Null entries mean "no tag for this slot". When present,
   * takes precedence over `menuItemId` for per-slot caption generation and
   * cross-post attribution. Omit for single-photo posts.
   */
  slotTags?: Array<number | null>;
  /**
   * Task #3660 — Per-slot caption ("Name — description") for a multi-item
   * "Daily specials" carousel, parallel to `mediaUrls`. Each slide is labelled
   * by its OWN tagged item's name + description; null entries are unlabelled
   * slots. Persisted onto each slot's `reviewMedia.caption` so the Eatzy feed
   * shows every slide's own name + description. Omit for non-specials posts.
   */
  slotCaptions?: Array<string | null>;
  /**
   * Task #3327 — Per-slot carousel focal point, parallel to `mediaUrls`. Each
   * entry is a normalized `{ x, y }` (each in `[0,1]`) or null (centre). Shared
   * across platforms because a carousel publishes at one derived aspect; the
   * server crops each slot's window around its focal point instead of centring.
   * Omit for single-photo posts (which use `platformCropPositions`).
   */
  carouselCropPositions?: Array<{ x: number; y: number } | null>;
  /** Social platforms to publish to. Eatzy is always-on and must not be listed.
   *  Pass an empty array for an Eatzy-only post. */
  platforms: Array<"instagram" | "facebook" | "tiktok">;
  /** Post type (e.g. `"daily_special"`, `"event"`). Defaults to `"daily_special"`. */
  postType?: string;
  /** Object-storage URL of the uploaded master video clip. */
  videoMasterUrl?: string;
  /**
   * Task #3559 — server-generated video poster URL the client captured from the
   * upload-cdn response. The server validates it against the Eatzy CDN
   * allow-list and uses it as the primary cover frame (falling back to the
   * transcode cover when absent or off-host).
   */
  videoThumbnailUrl?: string;
  trimSpec?: {
    startMs: number;
    endMs: number;
    /** Defaults to `startMs` (first frame). */
    coverMs?: number | undefined;
  };
  // Task #2225 — offer fields (coupon or Kudoz bonus).
  couponCode?: string;
  offerDiscountType?: string | undefined;
  kudozBonus?: number | "2x";
  offerTitle?: string;
  offerExpiresAt?: string;
  /**
   * Task #2251 — Naive ISO datetime string ("YYYY-MM-DDTHH:mm") in the
   * restaurant's local timezone. When provided, the post is created with
   * `status: "scheduled"` and the server uses `eatzyTimezone` for conversion.
   */
  localScheduledFor?: string;
  /**
   * Whether Instagram should publish the hashtag block as a first comment
   * (true, server default) instead of inline in the caption. Only honoured
   * for Instagram. Mirrors `composerStore.hashtagsInFirstComment` on mobile.
   */
  firstCommentHashtags?: boolean;
  /**
   * Task #3304 — explicit per-platform publish format chosen by the composer
   * (feed/reel/story/video), keyed by base platform. Only explicit entries
   * change server routing; absent entries fall through to the existing
   * default (feed photo / reel video / video). The server re-validates each
   * requested (platform, format) against the publish plan before fan-out.
   */
  platformFormats?: Partial<
    Record<
      "instagram" | "facebook" | "tiktok",
      "feed" | "reel" | "story" | "video"
    >
  >;
  /**
   * Task #3304 — explicit per-platform crop override for single-photo feed
   * legs, keyed by base platform. "1:1"/"4:5" center-crop the hero before
   * publish; "free" leaves the master image as-is.
   */
  platformCrops?: Partial<
    Record<"instagram" | "facebook", "1:1" | "4:5" | "free">
  >;
  /**
   * Task #3323 — explicit per-platform pan/zoom crop RECTANGLE chosen by the
   * composer's framing control, keyed by base platform. Each value is a
   * {@link CropRect} of integer pixels in the published hero image's
   * EXIF-corrected coordinate space — the exact rectangle the partner panned/
   * zoomed to within the chosen aspect. The server bakes this rectangle into
   * that platform's feed photo. Precedence on the server is:
   * explicit rect (`platformCropOverrides`) > `framingByPlatform` aspect >
   * legacy `platformCrops`. Photo posts only; Eatzy/TikTok are never keyed.
   */
  platformCropOverrides?: Partial<Record<"instagram" | "facebook", CropRect>>;
  /**
   * Task #3999 — per-platform FIT MODE for single-photo feed legs, keyed by
   * base platform. "cover" (default; absent key) keeps the pan/zoom crop
   * behaviour; "contain" makes the server composite the whole trimmed hero
   * centred on the platform canvas, filling the empty bands per
   * `platformBackgroundFills`. Photo posts only; Eatzy/TikTok are never keyed.
   */
  platformFitModes?: Partial<
    Record<"instagram" | "facebook", "cover" | "contain">
  >;
  /**
   * Task #3999 — background fill used behind the photo when the platform's
   * fit mode is "contain": "blur" (default; absent key) = blurred scaled copy
   * of the same photo; "white"/"black" = solid canvas. Ignored in "cover".
   */
  platformBackgroundFills?: Partial<
    Record<"instagram" | "facebook", "blur" | "white" | "black">
  >;
  /**
   * Task #3329 — explicit per-platform pan/zoom crop RECTANGLES for a
   * multi-photo CAROUSEL, keyed by base platform. Each value is an array
   * parallel to `mediaUrls`: index i holds the {@link CropRect} (EXIF-corrected
   * master pixels of carousel slot i's photo) the partner panned/zoomed to for
   * that slot on that platform, or null when the slot has no override. The
   * server bakes each slot's photo for the platform from its rect, taking
   * precedence over aspect framing for that slot. A null/absent slot entry — or
   * an absent platform key — keeps the slot's existing (uncropped) carousel
   * behaviour. Carousel analogue of `platformCropOverrides`; TikTok is never
   * keyed.
   *
   * Task #4139 — an `eatzy` key carries the per-slot crops authored on the
   * Eatzy tab; the server bakes each Eatzy carousel slot from its rect before
   * writing the Eatzy feed media rows.
   */
  carouselCropOverrides?: Partial<
    Record<"instagram" | "facebook" | "eatzy", (CropRect | null)[]>
  >;
  /**
   * Task #3322 — per-platform focal point paired with `platformCrops`, keyed by
   * base platform. `{ x, y }` are normalized to `[0,1]`; centre (0.5/0.5)
   * reproduces the legacy center-crop. Only meaningful with a "1:1"/"4:5" crop.
   */
  platformCropPositions?: Partial<
    Record<"instagram" | "facebook", { x: number; y: number }>
  >;
  /**
   * Task #3312 — Per-platform photo framing choice the partner picked via the
   * "Adjust framing" control in the mobile composer, keyed by full platform
   * name. Each value is an aspect token from the shared crop vocabulary
   * (`AspectRatio` in `packages/shared/src/types/photoEditSpec.ts`): `"4:5"`,
   * `"1:1"`, `"9:16"`, `"4:3"`, or `"free"` (no fixed crop). Absent keys mean
   * "use the platform default framing". Photo posts only; Eatzy is never keyed.
   */
  framingByPlatform?: Partial<
    Record<"instagram" | "facebook" | "tiktok", string>
  >;
  /**
   * Task #3617 — background music selection for VIDEO posts, mirroring the web
   * AudioStep. `audioTrackId` is a track id from `GET /api/partner/audio/tracks`
   * (curated + the partner's own uploads). When present, the server persists the
   * selection on the reviews row and the cross-poster muxes the track into the
   * video at publish. Absent / null means "no music" (no mux). Ignored for photo
   * posts. Knobs: `audioVolume` 0–150 (music %), `audioOriginalVolume` 0–100
   * (original clip audio kept under the music), `audioFadeMs` 0–5000 (fade len).
   */
  audioTrackId?: string;
  audioVolume?: number;
  audioOriginalVolume?: number;
  audioFadeMs?: number;
  /** Legacy ID-only correlation; new callers must use smartFixEvaluations. */
  smartFixEvaluationIds?: SmartFixEvaluationIdList;
  /** Exact native correlation projection; outcomes remain server-authored. */
  smartFixEvaluations?: SmartFixPublicationCorrelationList;
}

/** Response from `POST /api/partner/mobile/upload/start`. */
export interface MobileUploadStartResponse {
  sessionId: string;
  objectPath: string;
  chunkSize: number;
  expiresAt: string;
}

/** Response from `POST /api/partner/mobile/upload/:sessionId/chunk`. */
export interface MobileUploadChunkResponse {
  uploadedChunks: number;
  totalChunks: number | null;
  uploadedBytes: number;
  totalBytes: number | null;
}

/** Response from `POST /api/partner/mobile/upload/:sessionId/complete`. */
export interface MobileUploadCompleteResponse {
  finalUrl: string;
}

/**
 * Module for mobile background upload + cross-platform publish (Task #1009).
 *
 * Resumable upload flow:
 *   1. `startUpload` — declare a session; receive `sessionId` + `chunkSize`.
 *   2. `uploadChunk` — send chunks one by one (retry safe — use the same index).
 *   3. `completeUpload` — assemble and receive `finalUrl`.
 *   4. `publish` — create the post and fan out; use `finalUrl` as `mediaUrls[0]`.
 *   5. `getStatus` — poll until `overallStatus` is `"live"` or `"failed"`.
 *   6. `retryPlatform` — let the partner manually retry a single failed leg.
 *
 * The native library `react-native-background-upload` (decision #34) calls
 * `uploadChunk` for each chunk using the system-level upload task, so the
 * upload survives backgrounding and screen-lock (decision #21).
 */
export interface MobilePublishModule {
  /**
   * Initiate a new chunked-upload session.
   * Maps to `POST /api/partner/mobile/upload/start`.
   *
   * Pass `idempotencyKey` (a UUID generated once per post attempt) so that
   * retries after app crashes reuse the existing live session instead of
   * creating a new one.
   */
  startUpload(body: {
    restaurantId: string;
    mediaType: "image" | "video";
    mimeType?: string;
    totalBytes?: number;
    totalChunks?: number;
    idempotencyKey?: string;
  }): Promise<MobileUploadStartResponse>;

  /**
   * Upload a single binary chunk.
   * Maps to `POST /api/partner/mobile/upload/:sessionId/chunk?index=N`.
   * Idempotent — re-sending the same index overwrites the previous chunk.
   */
  uploadChunk(
    sessionId: string,
    chunkIndex: number,
    chunkData: ArrayBuffer | Uint8Array
  ): Promise<MobileUploadChunkResponse>;

  /**
   * Assemble all uploaded chunks into the final object.
   * Maps to `POST /api/partner/mobile/upload/:sessionId/complete`.
   */
  completeUpload(sessionId: string): Promise<MobileUploadCompleteResponse>;

  /**
   * Cancel an in-progress upload and clean up storage.
   * Maps to `POST /api/partner/mobile/upload/:sessionId/abort`.
   */
  abortUpload(sessionId: string): Promise<void>;

  /**
   * Create a post and fan out to all listed platforms.
   * Returns 202 with per-platform statuses all set to `"queued"`.
   * Poll `getStatus` to track progress.
   * Maps to `POST /api/partner/mobile/posts`.
   */
  publish(body: MobilePublishBody): Promise<MobilePublishResponse>;

  /**
   * Poll per-platform publish statuses for a post.
   * Maps to `GET /api/partner/mobile/posts/:postId/status`.
   */
  getStatus(postId: string): Promise<MobilePostStatusResponse>;

  /**
   * Manually retry a single failed platform leg.
   * Decision #22: no auto-retry. The partner must explicitly tap Retry.
   * Maps to `POST /api/partner/mobile/posts/:postId/retry/:platform`.
   */
  retryPlatform(
    postId: string,
    platform: Exclude<MobilePublishPlatform, "eatzy">
  ): Promise<{ queued: boolean; partnerPostId: string; platform: string }>;

  /**
   * Fetch today's posts for the partner's restaurant with per-platform statuses.
   * Drives the Status Dashboard feed and summary strip.
   *
   * Maps to `GET /api/partner/mobile/posts/today`.
   *
   * @param date  Optional ISO date string (YYYY-MM-DD). Defaults to today UTC.
   * @param tz    Optional IANA timezone (e.g. "America/New_York"). When provided
   *              the server interprets `date` in that timezone so day boundaries
   *              match the restaurant's local clock rather than UTC.
   */
  todaysFeed(date?: string, tz?: string): Promise<MobileTodayFeedResponse>;

  /**
   * Fetch per-day post-count summaries for a date window.
   * Drives the History heatmap in the mobile dashboard.
   *
   * Maps to `GET /api/partner/mobile/posts/range?from=...&to=...`.
   *
   * @param from  ISO date string (YYYY-MM-DD), inclusive start.
   * @param to    ISO date string (YYYY-MM-DD), inclusive end. Max 93 days from `from`.
   * @param tz    Optional IANA timezone. When provided the server interprets the
   *              `from`/`to` bounds in that timezone and buckets each post's
   *              `createdAt` into the local calendar day rather than UTC.
   */
  postRange(
    from: string,
    to: string,
    tz?: string
  ): Promise<MobilePostRangeResponse>;

  /**
   * Fetch per-day post-count summaries for an entire calendar month.
   *
   * Convenience wrapper around `postRange` that automatically computes the
   * first and last day of the given month. The response shape is identical to
   * `MobilePostRangeResponse` — days absent from the array have zero posts.
   *
   * Used by the `CalendarPickerModal` to render colour-coded status dots on
   * each day cell (green = all live, amber = pending, red = at least one
   * failure, no dot = no posts that day).
   *
   * Maps to `GET /api/partner/mobile/posts/range?from=YYYY-MM-01&to=YYYY-MM-DD`.
   *
   * @param month  ISO month string (YYYY-MM). Future months return an empty day array.
   * @param tz     Optional IANA timezone forwarded to `postRange`. When provided
   *               dots appear on the day the partner experienced the publish, not
   *               the UTC day.
   */
  calendarSummary(month: string, tz?: string): Promise<MobilePostRangeResponse>;

  /**
   * Retrieve the web-portal OAuth reconnect URL for the given platform with the
   * partner's restaurantId embedded by the server. The mobile app opens this
   * URL in the system browser; the web portal owns the OAuth handshake.
   *
   * Maps to `GET /api/partner/mobile/reconnect-url?platform=...&requestedPlatform=...&returnTo=...`.
   *
   * @param platform  "instagram" | "facebook" | "tiktok"
   * @param requestedPlatformOrProvider Legacy third argument. Prefer `options`
   *                           so provider and requested platform cannot be
   *                           conflated.
   * @param returnTo  Deep-link the portal should bounce to on OAuth success.
   */
  reconnectUrl(
    platform: string | undefined,
    returnTo: string,
    requestedPlatformOrProvider?: string,
    options?: MobileReconnectUrlOptions
  ): Promise<{ url: string }>;

  /**
   * Fetch all scheduled posts for the partner's restaurant.
   * Drives the Scheduled section on the Posts screen.
   *
   * Maps to `GET /api/partner/mobile/scheduled`.
   */
  listScheduled(): Promise<MobileScheduledFeedResponse>;

  /**
   * Cancel a scheduled post by reverting its status to "draft".
   * The post remains in the DB and can be re-scheduled from the web portal.
   *
   * Maps to `DELETE /api/partner/mobile/scheduled/:postId`.
   */
  cancelScheduled(
    postId: string
  ): Promise<{ success: boolean; postId: string; status: string }>;

  /**
   * Update the scheduled publish time for a scheduled post.
   *
   * `localScheduledFor` is a naive ISO datetime string ("YYYY-MM-DDTHH:mm")
   * in the restaurant's local timezone, interpreted server-side using the
   * restaurant's IANA `eatzyTimezone`. Must be at least 10 minutes in the
   * future.
   *
   * Maps to `PATCH /api/partner/mobile/scheduled/:postId`.
   */
  rescheduleScheduled(
    postId: string,
    localScheduledFor: string
  ): Promise<{ success: boolean; postId: string; scheduledFor: string }>;

  /**
   * Hard-delete a non-live partner post by ID.
   *
   * Live posts (status = "live") cannot be deleted and the server will
   * reject the request with 409.  For all other statuses (draft, pending,
   * scheduled, failed …) the post and its platform-status rows are
   * permanently removed from the database.
   *
   * Returns `{ success: true, wasScheduled }` where `wasScheduled` signals
   * whether the deleted post was in the "scheduled" state — callers should
   * invalidate the calendarSummary cache when this is `true`.
   *
   * Maps to `DELETE /api/partner/mobile/posts/:postId`.
   */
  deletePost(postId: string): Promise<{ success: true; wasScheduled: boolean }>;
}

// ─────────────────────────────────────────────────────────────────────────────
// ApiClient surface
// ─────────────────────────────────────────────────────────────────────────────

export interface ApiClient {
  readonly config: ApiClientConfig;
  readonly storage: TokenStorage;
  readonly auth: AuthModule;
  readonly user: UserModule;
  readonly partner: {
    readonly posts: PartnerPostsModule;
    readonly menu: PartnerMenuModule;
    readonly audio: PartnerAudioModule;
    readonly restaurants: PartnerRestaurantsModule;
    readonly notifications: PartnerNotificationsModule;
    readonly dashboard: PartnerDashboardModule;
    readonly social: PartnerSocialModule;
    readonly video: PartnerVideoModule;
    readonly mobilePublish: MobilePublishModule;
  };
  readonly account: AccountModule;
  /** Phase 2: typed multipart media upload (Task #1020). */
  readonly media: MediaModule;
  readonly shortLinks: ShortLinksModule;
  /**
   * Low-level escape hatch for endpoints not yet promoted to the typed
   * surface. Passes through the full auth-refresh-and-retry pipeline.
   * An optional signal aborts transport and remains attached on auth retry.
   */
  raw<T = unknown>(
    method: string,
    path: string,
    body?: unknown,
    extraHeaders?: Record<string, string>,
    signal?: AbortSignal,
    requestIdOverride?: string,
    /** Rechecked after credential reads and before transport/auth retry. */
    assertRequestCurrent?: () => void
  ): Promise<T>;
}

// ─────────────────────────────────────────────────────────────────────────────
// ApiError
// ─────────────────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly body?: unknown;

  constructor(message: string, status: number, code?: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    if (code !== undefined) this.code = code;
    if (body !== undefined) this.body = body;
  }
}

/**
 * Build a human-readable message for an `ApiError`.
 *
 * When a bare-proxy 502/503/504 returns no body the resolved `message`
 * string falls back to `String(status)` (empty `statusText` on many
 * environments). Naïvely formatting that as `"${status}: ${message}"` yields
 * the confusing "502: 502" pattern that partners see in the error banner.
 * This helper detects the fallback case and substitutes a plain English string.
 */
function buildApiErrorMessage(status: number, message: string): string {
  return message && message !== String(status)
    ? `${status}: ${message}`
    : `Server error (${status}) — please try again`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Photo/media upload retry
//
// A single transient 502/503/504 (or network blip) during a photo media upload
// should not surface the error banner immediately — the same Cloudflare/ingress
// layer affects photo uploads the same way it affects video master uploads.
// We retry up to 3 times with an exponential back-off (1 s → 3 s → 6 s),
// honouring the caller's AbortSignal so a partner cancel still stops immediately.
// Mirrors the UPLOAD_RETRY_DELAYS_MS pattern in videoPipelineRunner.ts.
// ─────────────────────────────────────────────────────────────────────────────

const MEDIA_UPLOAD_RETRY_DELAYS_MS = [1_000, 3_000, 6_000] as const;
const MEDIA_UPLOAD_RETRYABLE_STATUSES = new Set([502, 503, 504]);

function isRetryableMediaUploadError(err: unknown): boolean {
  if (err instanceof ApiError) {
    // Abort is terminal — never retry a cancelled upload.
    if (err.code === "upload_aborted") return false;
    // 502/503/504 gateway errors and bare network failures (status 0) are transient.
    return MEDIA_UPLOAD_RETRYABLE_STATUSES.has(err.status) || err.status === 0;
  }
  // Plain AbortError from the signal — terminal.
  if (err instanceof Error && err.name === "AbortError") return false;
  // Any other thrown Error (e.g. a transport rejection) is worth retrying.
  if (err instanceof Error) return true;
  return false;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal HTTP core
// ─────────────────────────────────────────────────────────────────────────────

const WRITE_METHODS = new Set(["POST", "PUT", "PATCH"]);

/**
 * Task #2674 — client-generated correlation id sent as `X-Request-Id`.
 *
 * The server's requestIdMiddleware echoes a caller-supplied `X-Request-Id`, so
 * sending one here lets a structured server log line (e.g. the partner-mutation
 * "unresolved" 400) be correlated 1:1 with the on-device navTrace breadcrumb for
 * the same flow — the only bridge available without production device access.
 *
 * Deliberately dependency-free: no crypto.randomUUID (unreliable under Hermes
 * per the polyfill history) — just time + entropy, which is collision-safe for
 * correlation purposes.
 */
export function makeRequestId(): string {
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Detects a Cloudflare anti-bot challenge response.
 *
 * Cloudflare returns its "Just a moment…" interstitial as an HTML body
 * (typically HTTP 403, sometimes 503/429) BEFORE the request ever reaches our
 * Express server. A large multipart file POST is far more likely to trip
 * Cloudflare's Managed Challenge / Bot Fight Mode than ordinary JSON traffic —
 * the response then has no JSON `error` field, so the partner only sees a bare
 * "403:" with no explanation.
 *
 * This is the single source of truth for the heuristic; `backgroundUpload.ts`
 * re-exports it so the OS-background-session upload path and the foreground
 * `uploadMaster` XHR path classify identically.
 *
 * The authoritative fix is an infra change — a Cloudflare WAF Skip rule for the
 * upload path. See the operator runbook in
 * `apps/web/server/middleware/auth.ts`. This client-side detection only turns
 * the raw challenge HTML into a readable error + a diagnostic code so the
 * failure is recognisable in the field.
 */
/**
 * Diagnostic Cloudflare response headers.
 *
 * - `cfMitigated` (`cf-mitigated`) names the action the edge took, e.g.
 *   `"challenge"`. It is ONLY present when Cloudflare actively mitigated the
 *   request, so it is the authoritative challenge signal.
 * - `cfRay` (`cf-ray`) identifies the exact edge request. It is present on
 *   essentially EVERY Cloudflare-proxied response (success and failure), so it
 *   is for REPORTING/correlation only and must never be used on its own to
 *   decide that a response was a challenge.
 */
export interface CloudflareSignals {
  cfRay: string | null;
  cfMitigated: string | null;
}

export function isCloudflareChallenge(
  status: number,
  body: string,
  headers?: CloudflareSignals | null
): boolean {
  if (status !== 403 && status !== 503 && status !== 429) return false;
  // Header signal (authoritative). A Managed Challenge or Browser Integrity
  // Check can arrive with an EMPTY body — the body markers below then match
  // nothing and the partner only sees a bare "403:". But Cloudflare always
  // sets `cf-mitigated` naming the action it took, so trust that first.
  const mitigated = (headers?.cfMitigated || "").trim().toLowerCase();
  if (mitigated && mitigated !== "none") return true;
  const b = (body || "").toLowerCase();
  return (
    b.includes("just a moment") ||
    b.includes("cf-browser-verification") ||
    b.includes("challenge-platform") ||
    b.includes("/cdn-cgi/") ||
    // Browser Integrity Check / IP block interstitials.
    b.includes("you have been blocked") ||
    b.includes("attention required")
  );
}

/** Read a single response header from an XMLHttpRequest. Never throws. */
function readXhrHeader(xhr: XMLHttpRequest, name: string): string | null {
  try {
    return xhr.getResponseHeader(name);
  } catch {
    return null;
  }
}

/** Read a single response header from a fetch Response. Never throws. */
function readResHeader(res: Response, name: string): string | null {
  try {
    return res.headers?.get(name) ?? null;
  } catch {
    return null;
  }
}

/**
 * Returns true for body values that fetch should send as raw bytes
 * rather than serialise via `JSON.stringify`. Used by chunked-upload
 * endpoints (Task #1063) so partner mobile can ship media bytes
 * directly through the typed api-client.
 */
function isBinaryBodyValue(body: unknown): boolean {
  if (body == null) return false;
  if (body instanceof ArrayBuffer) return true;
  if (ArrayBuffer.isView(body)) return true;
  if (typeof Blob !== "undefined" && body instanceof Blob) return true;
  if (typeof FormData !== "undefined" && body instanceof FormData) return true;
  return false;
}

const AUTH_ERROR_CODES = new Set([
  "TOKEN_EXPIRED",
  "INVALID_TOKEN",
  "UNAUTHORIZED",
  "KUDOZ_TOKEN_EXPIRED",
]);

/**
 * Paths that must NOT trigger the silent refresh-and-retry loop.
 */
function isRefreshExempt(path: string): boolean {
  return (
    path.includes("/api/auth/refresh-kudoz-token") ||
    path.includes("/api/auth/refresh-eatzy-token") ||
    path.includes("/api/auth/sign-in") ||
    path.includes("/api/auth/sign-up") ||
    path.includes("/api/auth/sign-out") ||
    path.includes("/api/auth/forgot-password") ||
    path.includes("/api/auth/reset-password") ||
    // Task #2776 — credential-based Eatzy mint. Its 401 means "Eatzy auth
    // failed", never "Kudoz session expired", so it must NOT trigger a kudoz
    // refresh (a failed kudoz refresh would call onAuthExpired and sign the
    // partner out spuriously).
    path.includes("/api/partner/restmanager/auth")
  );
}

/**
 * Decide whether a non-ok response warrants a silent token refresh.
 */
async function detectRefreshNeeded(
  res: Response
): Promise<"kudoz" | "eatzy" | null> {
  if (res.status !== 401 && res.status !== 403) return null;
  let body: Record<string, unknown> = {};
  try {
    body = (await res.clone().json()) as Record<string, unknown>;
  } catch {
    // ignore parse errors
  }
  if (body.eatzyTokenExpired) return "eatzy";
  if (res.status === 401) return "kudoz";
  const code = (body.code as string | undefined) ?? "";
  if (AUTH_ERROR_CODES.has(code)) return "kudoz";
  return null;
}

async function throwIfNotOk(res: Response): Promise<void> {
  if (res.ok) return;

  // Task #1838 — gateway errors (502 Bad Gateway, 503 Service Unavailable,
  // 504 Gateway Timeout) are produced by the load balancer when the app
  // server is temporarily unreachable (e.g. a rolling restart). The body is
  // typically an empty or HTML gateway page that produces "502: " when parsed
  // normally. Surface a plain-English message instead.
  //
  // Exception: when the server itself returns a 502 with a JSON body that
  // contains a specific `error` field (e.g. cross-menu move failure returns
  // 502 with { error: "Couldn't move item — please try again." }), we must
  // surface that message rather than the generic gateway fallback.
  if (res.status === 502 || res.status === 503 || res.status === 504) {
    // Task #4021 — a Cloudflare Managed Challenge can arrive as a 503 with an
    // HTML (or empty) body and a `cf-mitigated` header. Classify it BEFORE the
    // generic gateway fallback so a WAF block is never misreported as "server
    // temporarily unavailable" (which suggests waiting will fix it — it won't).
    if (res.status === 503) {
      const cfRay503 = readResHeader(res, "cf-ray");
      const cfMitigated503 = readResHeader(res, "cf-mitigated");
      let text503 = "";
      try {
        text503 = await res.clone().text();
      } catch {
        // ignore — header signal alone still classifies
      }
      if (
        isCloudflareChallenge(res.status, text503, {
          cfRay: cfRay503,
          cfMitigated: cfMitigated503,
        })
      ) {
        throw new ApiError(
          "Request was blocked by a security check (CF-challenge). Please try again.",
          res.status,
          "CLOUDFLARE_CHALLENGE",
          {
            cfRay: cfRay503,
            cfMitigated: cfMitigated503,
            nonJsonResponse: true,
            htmlBodyPreview: text503.slice(0, 500),
          }
        );
      }
    }
    const ct502 = res.headers.get("content-type") ?? "";
    if (ct502.includes("application/json")) {
      try {
        const body502 = (await res.clone().json()) as Record<string, unknown>;
        const specificMsg =
          (body502.error as string | undefined) ||
          (body502.message as string | undefined);
        if (specificMsg && specificMsg.trim()) {
          throw new ApiError(specificMsg.trim(), res.status, "GATEWAY_ERROR");
        }
      } catch (jsonErr) {
        if (jsonErr instanceof ApiError) throw jsonErr;
        // body wasn't valid JSON — fall through to the generic message
      }
    }
    throw new ApiError(
      "Server is temporarily unavailable — please try again in a moment.",
      res.status,
      "GATEWAY_ERROR"
    );
  }

  const ct = res.headers.get("content-type") ?? "";
  let body: Record<string, unknown> = {};
  let text = "";
  if (ct.includes("application/json")) {
    try {
      body = (await res.clone().json()) as Record<string, unknown>;
    } catch {
      // ignore
    }
  } else {
    text = (await res.text()) || res.statusText;
  }

  // Task #1673 — never let server-rendered HTML (e.g. Replit's "Run this
  // app to see the results here" 404 placeholder when the dev server is
  // hibernating, or a reverse-proxy maintenance page) leak through to
  // user-facing UI as the error message. Detect HTML by content-type or
  // by sniffing the body, replace the message with a short user-safe
  // string, and stash the raw body on the error's `details` so Bugsnag
  // breadcrumbs / network logs can still surface the real cause.
  const looksLikeHtml =
    ct.includes("text/html") ||
    /^\s*<(?:!doctype|html|head|body)\b/i.test(text);
  const rawBodyForDetails = text;

  // Task #4021 — classify Cloudflare Managed Challenge / bot-block responses
  // on the SHARED JSON request path (not just the upload XHR paths). A GET
  // like /api/partner/mobile/posts/range can be challenged by CF in the field,
  // returning HTML (or an empty body + `cf-mitigated` header) with 403/503/429.
  // Without this branch the caller only sees a generic "unexpected response"
  // and nobody can tell a WAF block from a server bug. The authoritative fix
  // is a Cloudflare WAF Skip rule (operator runbook in
  // `apps/web/server/middleware/auth.ts`); this code makes the failure
  // recognisable via `ApiError.code === "CLOUDFLARE_CHALLENGE"`.
  const cfRay = readResHeader(res, "cf-ray");
  const cfMitigated = readResHeader(res, "cf-mitigated");
  if (isCloudflareChallenge(res.status, text, { cfRay, cfMitigated })) {
    throw new ApiError(
      "Request was blocked by a security check (CF-challenge). Please try again.",
      res.status,
      "CLOUDFLARE_CHALLENGE",
      {
        cfRay,
        cfMitigated,
        nonJsonResponse: true,
        htmlBodyPreview: rawBodyForDetails.slice(0, 500),
      }
    );
  }

  if (looksLikeHtml) {
    text =
      res.status === 404
        ? "Server returned a non-JSON response. Please check your connection or try again later."
        : "Server returned an unexpected response. Please try again.";
  }

  const message =
    (body.error as string) ||
    (body.message as string) ||
    (body.details as string) ||
    text ||
    res.statusText ||
    String(res.status);
  const code =
    (body.code as string | undefined) ?? (body.errorCode as string | undefined);
  const details: Record<string, unknown> = { ...body };
  if (looksLikeHtml && rawBodyForDetails) {
    // Truncate so we never push a 50 KB HTML blob into Bugsnag breadcrumbs.
    details.htmlBodyPreview = rawBodyForDetails.slice(0, 500);
    details.nonJsonResponse = true;
  }
  throw new ApiError(
    buildApiErrorMessage(res.status, message),
    res.status,
    code,
    details
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Core client factory
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build the low-level request function and token-refresh helpers that are
 * shared by all the typed module factories below.
 *
 * The refresh helpers are defined here (not in the AuthModule) so they can
 * be called from inside the retry loop inside `request()` without circular
 * dependencies.
 */
function buildCoreHttp(config: ApiClientConfig) {
  const f = config.fetch ?? globalThis.fetch;
  const { storage, clientHeader, baseUrl } = config;

  /**
   * Emit a body-free, token-free request breadcrumb to the configured
   * observer. Never throws back into the request path (Task #2644).
   */
  function emitObserve(
    method: string,
    path: string,
    status: number,
    startedAt: number,
    res: Response | null,
    errorCode: string | null
  ): void {
    if (!config.requestObserver) return;
    let requestId: string | null;
    try {
      requestId = res?.headers?.get("x-request-id") ?? null;
    } catch {
      requestId = null;
    }
    try {
      config.requestObserver({
        method,
        // Strip any query string defensively — paths here never carry one,
        // but a leaked token in a query param must never reach an observer
        // (which may be a third-party analytics adapter, not just the local
        // diagnostics ring).
        path: redactSensitive(path.split("#", 1)[0].split("?", 1)[0]),
        status,
        durationMs: Date.now() - startedAt,
        requestId,
        errorCode,
      });
    } catch {
      // never let diagnostics observation throw into the caller
    }
  }

  /**
   * Internal: refresh the Kudoz JWT. Uses the current (potentially
   * stale) token as the Bearer credential, matching the server's
   * /api/auth/refresh-kudoz-token contract.
   * Returns true on success, false otherwise. Never throws.
   */
  let kudozRefreshInFlight: Promise<boolean> | null = null;

  async function _refreshKudoz(): Promise<boolean> {
    if (kudozRefreshInFlight) return kudozRefreshInFlight;
    const token = await storage.getKudozToken();
    if (!token) return false;

    kudozRefreshInFlight = (async () => {
      try {
        const res = await f(`${baseUrl}/api/auth/refresh-kudoz-token`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-Client": clientHeader,
          },
        });
        if (!res.ok) return false;
        const data = (await res.json()) as AuthRefreshKudozResponse;
        if (!data?.token) return false;
        await storage.setKudozToken(data.token);
        return true;
      } catch {
        return false;
      } finally {
        setTimeout(() => {
          kudozRefreshInFlight = null;
        }, 1000);
      }
    })();

    return kudozRefreshInFlight;
  }

  /**
   * Internal: refresh the Eatzy access token using the stored refresh token.
   * Returns true on success, false otherwise. Never throws.
   *
   * Uses the same shared in-flight Promise pattern as `_refreshKudoz` so
   * concurrent callers always wait for the single real network request rather
   * than timing out against an arbitrary sleep.
   */
  let eatzyRefreshInFlight: Promise<boolean> | null = null;

  async function _refreshEatzy(): Promise<boolean> {
    if (eatzyRefreshInFlight) return eatzyRefreshInFlight;
    const refreshToken = await storage.getEatzyRefreshToken();
    if (!refreshToken) return false;

    eatzyRefreshInFlight = (async () => {
      try {
        const res = await f(`${baseUrl}/api/auth/refresh-eatzy-token`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Client": clientHeader,
          },
          body: JSON.stringify({ refreshToken }),
        });
        if (!res.ok) return false;
        const data = (await res.json()) as AuthRefreshEatzyResponse;
        if (!data.eatzyAccessToken) return false;
        await storage.setEatzyToken(data.eatzyAccessToken);
        if (data.eatzyTokenExpires != null) {
          await storage.setEatzyTokenExpiry(data.eatzyTokenExpires);
        }
        return true;
      } catch {
        return false;
      } finally {
        setTimeout(() => {
          eatzyRefreshInFlight = null;
        }, 1000);
      }
    })();

    return eatzyRefreshInFlight;
  }

  /**
   * Main request function. Adds auth, X-Client, attribution, and partner
   * headers, then executes the fetch. On 401/403 performs one
   * refresh-and-retry before calling `onAuthExpired`.
   */
  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    extraHeaders?: Record<string, string>,
    signal?: AbortSignal,
    requestIdOverride?: string,
    assertRequestCurrent?: () => void
  ): Promise<T> {
    const url = `${baseUrl}${path}`;
    const upperMethod = method.toUpperCase();
    const isPartner = path.includes("/api/partner/");
    const isBinaryBody = isBinaryBodyValue(body);
    const startedAt = Date.now();
    // Task #2674 — stable per-call correlation id (kept across the 401 refresh
    // retry so both attempts share one id on the server). Task #2706 — a caller
    // may pass an explicit id so a multi-request flow (e.g. media upload →
    // link-photo) shares ONE id across both server log lines. Used for both the
    // wire header AND the diagnostics observer so they never diverge.
    const requestId = requestIdOverride ?? makeRequestId();

    const buildInit = async (): Promise<RequestInit> => {
      const headers: Record<string, string> = {
        "X-Client": clientHeader,
        "X-Request-Id": requestId,
        ...(body !== undefined && !isBinaryBody
          ? { "Content-Type": "application/json" }
          : {}),
      };

      // Authorization
      const kudozToken = await storage.getKudozToken();
      if (kudozToken) headers["Authorization"] = `Bearer ${kudozToken}`;

      // Partner headers
      if (isPartner && config.partnerHeadersProvider) {
        Object.assign(headers, config.partnerHeadersProvider());
      }

      // Attribution
      if (WRITE_METHODS.has(upperMethod) && config.attributionProvider) {
        const ctx = redactAttributionContext(config.attributionProvider());
        if (ctx) headers["x-attribution-context"] = JSON.stringify(ctx);
      }

      // Caller-supplied overrides
      if (extraHeaders) Object.assign(headers, extraHeaders);

      // Binary bodies (ArrayBuffer / typed arrays / Blob) are sent as-is
      // so chunked upload endpoints receive raw bytes. JSON encoding is
      // only applied to plain object / scalar bodies.
      const rawBody: BodyInit | undefined =
        body === undefined
          ? undefined
          : isBinaryBody
            ? (body as BodyInit)
            : JSON.stringify(body);

      return {
        method: upperMethod,
        headers,
        body: rawBody ?? null,
        credentials: "include",
        signal: signal ?? null,
      };
    };

    // Stage label: NETWORK_REQUEST_FAILED. A throw here means the request never
    // produced an HTTP response (DNS, TLS, connection refused, RN "Network
    // request failed", or a broken fetch polyfill). Tagging it lets callers tell
    // a transport failure apart from a non-2xx HTTP status or a body-read crash.
    let res: Response;
    try {
      const init = await buildInit();
      assertRequestCurrent?.();
      res = await f(url, init);
    } catch (err) {
      emitObserve(
        upperMethod,
        path,
        0,
        startedAt,
        null,
        "NETWORK_REQUEST_FAILED"
      );
      const msg = err instanceof Error ? err.message : String(err);
      throw new ApiError(
        `Network request failed: ${msg}`,
        0,
        "NETWORK_REQUEST_FAILED",
        err
      );
    }

    assertRequestCurrent?.();
    if (!res.ok && !isRefreshExempt(path)) {
      const refreshKind = await detectRefreshNeeded(res);
      assertRequestCurrent?.();
      if (refreshKind) {
        const refreshed =
          refreshKind === "kudoz"
            ? await _refreshKudoz()
            : await _refreshEatzy();

        if (refreshed) {
          const init = await buildInit();
          assertRequestCurrent?.();
          res = await f(url, init);
        } else {
          config.onAuthExpired?.();
          throw new ApiError(
            refreshKind === "eatzy" ? "eatzyTokenExpired" : "Session expired",
            res.status,
            refreshKind === "eatzy"
              ? "EATZY_TOKEN_EXPIRED"
              : "KUDOZ_TOKEN_EXPIRED"
          );
        }
      }
    }

    emitObserve(
      upperMethod,
      path,
      res.status,
      startedAt,
      res,
      res.ok ? null : `HTTP_${res.status}`
    );

    await throwIfNotOk(res);

    // Stage label: RESPONSE_BODY_READ_FAILED. Reading the response body
    // (res.text()) is the first place a broken Hermes Response/Blob polyfill
    // surfaces — the HTTP response arrived OK but its body cannot be consumed.
    // Tagging it distinguishes this from a transport failure or a bad status.
    let text: string;
    try {
      text = await res.text();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      throw new ApiError(
        `Failed to read response body: ${msg}`,
        res.status,
        "RESPONSE_BODY_READ_FAILED",
        err
      );
    }

    // House Posts requires JSON. Report only a fixed classification, never body
    // content (captions), query parameters, or provider error details.
    if (/^\/api\/partner\/house-posts(?:[/?]|$)/.test(path)) {
      const mediaType = (res.headers.get("content-type") ?? "").split(";")[0]!.trim().toLowerCase();
      const code = mediaType !== "application/json" && !/^application\/[\w.+-]+\+json$/.test(mediaType)
        ? "HOUSE_POSTS_WRONG_CONTENT_TYPE"
        : (() => { try { JSON.parse(text); return null; } catch { return "HOUSE_POSTS_MALFORMED_JSON"; } })();
      if (code) {
        emitObserve(upperMethod, path, res.status, startedAt, res, code);
        throw new ApiError("House Posts could not be loaded. Please try again.", res.status, code);
      }
    }
    // Return parsed JSON body, or undefined for empty responses (204 etc.)
    if (!text) return undefined as unknown as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      return text as unknown as T;
    }
  }

  /**
   * Like `request()` but stops just before consuming the response body,
   * returning the raw `Response` after auth-refresh-and-retry.
   *
   * Use for streaming endpoints where the caller needs `res.body.getReader()`
   * (e.g. `generateCaptionStreaming`).  All auth, partner, attribution, and
   * X-Client headers are applied identically to `request()`.
   *
   * An `AbortSignal` can be forwarded from the caller so in-flight requests
   * are cancelled when the user navigates away from the screen.
   */
  async function requestRaw(
    method: string,
    path: string,
    body?: unknown,
    extraHeaders?: Record<string, string>,
    signal?: AbortSignal
  ): Promise<Response> {
    const url = `${baseUrl}${path}`;
    const upperMethod = method.toUpperCase();
    const isPartner = path.includes("/api/partner/");
    const startedAt = Date.now();
    // Task #2674 — stable per-call correlation id (see request()).
    const requestId = makeRequestId();

    const buildInit = async (): Promise<RequestInit> => {
      const headers: Record<string, string> = {
        "X-Client": clientHeader,
        "X-Request-Id": requestId,
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      };

      const kudozToken = await storage.getKudozToken();
      if (kudozToken) headers["Authorization"] = `Bearer ${kudozToken}`;

      if (isPartner && config.partnerHeadersProvider) {
        Object.assign(headers, config.partnerHeadersProvider());
      }

      if (WRITE_METHODS.has(upperMethod) && config.attributionProvider) {
        const ctx = redactAttributionContext(config.attributionProvider());
        if (ctx) headers["x-attribution-context"] = JSON.stringify(ctx);
      }

      if (extraHeaders) Object.assign(headers, extraHeaders);

      return {
        method: upperMethod,
        headers,
        body: body !== undefined ? JSON.stringify(body) : null,
        credentials: "include",
        signal: signal ?? null,
      };
    };

    let res: Response;
    try {
      res = await f(url, await buildInit());
    } catch (err) {
      emitObserve(
        upperMethod,
        path,
        0,
        startedAt,
        null,
        "NETWORK_REQUEST_FAILED"
      );
      throw err;
    }

    if (!res.ok && !isRefreshExempt(path)) {
      const refreshKind = await detectRefreshNeeded(res);
      if (refreshKind) {
        const refreshed =
          refreshKind === "kudoz"
            ? await _refreshKudoz()
            : await _refreshEatzy();

        if (refreshed) {
          // Re-build init with the same caller signal. If the caller aborted
          // before the retry fires, the retry will also abort — which is correct
          // (we should not retry a cancelled request).
          res = await f(url, await buildInit());
        } else {
          config.onAuthExpired?.();
          throw new ApiError(
            refreshKind === "eatzy" ? "eatzyTokenExpired" : "Session expired",
            res.status,
            refreshKind === "eatzy"
              ? "EATZY_TOKEN_EXPIRED"
              : "KUDOZ_TOKEN_EXPIRED"
          );
        }
      }
    }

    emitObserve(
      upperMethod,
      path,
      res.status,
      startedAt,
      res,
      res.ok ? null : `HTTP_${res.status}`
    );

    await throwIfNotOk(res);
    return res;
  }

  return { request, requestRaw, _refreshKudoz, _refreshEatzy };
}

// ─────────────────────────────────────────────────────────────────────────────
// Module factories
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Persist the auth tokens returned by a successful sign-in/sign-up.
 *
 * IMPORTANT: this runs AFTER the network call already succeeded (HTTP 2xx with a
 * token in the body). A failure here is therefore NOT a credential or network
 * problem — it is a device secure-storage failure. We wrap it in an `ApiError`
 * tagged `TOKEN_STORAGE_FAILED` so callers never report "incorrect email or
 * password" for what is actually a storage write that threw on valid creds.
 */
async function persistAuthTokens(
  storage: TokenStorage,
  data: AuthSignInResponse
): Promise<void> {
  try {
    await storage.setKudozToken(data.token);
    if (data.eatzyAccessToken) {
      await storage.setEatzyToken(data.eatzyAccessToken);
    }
    if (data.eatzyRefreshToken) {
      await storage.setEatzyRefreshToken(data.eatzyRefreshToken);
    }
    if (data.eatzyTokenExpires != null) {
      await storage.setEatzyTokenExpiry(data.eatzyTokenExpires);
    }
    if (data.eatzyRefreshExpires != null) {
      await storage.setEatzyRefreshExpiry(data.eatzyRefreshExpires);
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new ApiError(
      `Token persistence failed after successful sign-in: ${msg}`,
      0,
      "TOKEN_STORAGE_FAILED",
      err
    );
  }
}

function createAuthModule(
  config: ApiClientConfig,
  request: ReturnType<typeof buildCoreHttp>["request"],
  _refreshKudoz: ReturnType<typeof buildCoreHttp>["_refreshKudoz"],
  _refreshEatzy: ReturnType<typeof buildCoreHttp>["_refreshEatzy"]
): AuthModule {
  return {
    async signIn(email, password) {
      const data = await request<AuthSignInResponse>(
        "POST",
        "/api/auth/sign-in",
        { email, password }
      );
      await persistAuthTokens(config.storage, data);
      return data;
    },

    async signUp(email, password, name, opts) {
      const data = await request<AuthSignInResponse>(
        "POST",
        "/api/auth/sign-up",
        {
          email,
          password,
          name,
          ...(opts?.inviteToken ? { inviteToken: opts.inviteToken } : {}),
        }
      );
      await persistAuthTokens(config.storage, data);
      return data;
    },

    async signInWithApple(identityToken) {
      const data = await request<AuthSignInResponse>(
        "POST",
        "/api/auth/apple",
        { identityToken }
      );
      await persistAuthTokens(config.storage, data);
      return data;
    },

    async signInWithGoogle(idToken) {
      const data = await request<AuthSignInResponse>(
        "POST",
        "/api/auth/google",
        { idToken }
      );
      await persistAuthTokens(config.storage, data);
      return data;
    },

    async signOut() {
      try {
        await request<void>("POST", "/api/auth/sign-out", {});
      } finally {
        await config.storage.clear();
      }
    },

    refreshKudozToken: _refreshKudoz,
    refreshEatzyToken: _refreshEatzy,

    async mintEatzyToken(email, password) {
      try {
        const data = await request<RestmanagerAuthResponse>(
          "POST",
          "/api/partner/restmanager/auth",
          { email, password }
        );
        if (!data?.success || !data.accessToken) return false;
        await config.storage.setEatzyToken(data.accessToken);
        if (data.eatzyRefreshToken) {
          await config.storage.setEatzyRefreshToken(data.eatzyRefreshToken);
        }
        if (data.eatzyTokenExpires != null) {
          await config.storage.setEatzyTokenExpiry(data.eatzyTokenExpires);
        }
        if (data.eatzyRefreshExpires != null) {
          await config.storage.setEatzyRefreshExpiry(data.eatzyRefreshExpires);
        }
        return true;
      } catch {
        // A 401 here means the Eatzy credential auth failed; any other error
        // is a network/transport issue. Either way the caller decides how to
        // surface it — this helper never throws.
        return false;
      }
    },

    async mintEatzyTokenFromSession() {
      try {
        const data = await request<RestmanagerAuthResponse>(
          "POST",
          "/api/partner/restmanager/auth-sso"
        );
        if (!data?.success || !data.accessToken) return false;
        await config.storage.setEatzyToken(data.accessToken);
        if (data.eatzyRefreshToken) {
          await config.storage.setEatzyRefreshToken(data.eatzyRefreshToken);
        }
        if (data.eatzyTokenExpires != null) {
          await config.storage.setEatzyTokenExpiry(data.eatzyTokenExpires);
        }
        if (data.eatzyRefreshExpires != null) {
          await config.storage.setEatzyRefreshExpiry(data.eatzyRefreshExpires);
        }
        return true;
      } catch {
        // 401 = the Hub could not exchange the session; 503 = the Hub exchange
        // endpoint is not deployed yet. Both leave the device without an Eatzy
        // token; the caller surfaces a retry/error state. Never throws.
        return false;
      }
    },

    async session() {
      try {
        return await request<AuthSessionResponse>("GET", "/api/auth/session");
      } catch {
        return null;
      }
    },

    async role() {
      return request<Record<string, unknown>>("GET", "/api/user/role");
    },
  };
}

function createUserModule(
  request: ReturnType<typeof buildCoreHttp>["request"]
): UserModule {
  return {
    me() {
      return request<ApiUser>("GET", "/api/user");
    },
  };
}

// ─── Shared normalization for per-platform caption responses ─────────────────

/**
 * Normalise the raw server response from `POST /api/partner/posts/generate-caption`
 * into the typed `GenerateCaptionPerPlatformResponse` contract.
 *
 * The server may return:
 *   - `{ captions: string[] }` — legacy string-array shape
 *   - `{ captions: { warm: string, punchy: string, story: string } }` — named-key shape
 *   - `{ captions: GenerateCaptionVariant[] }` — modern typed-object shape
 *
 * All three are accepted and coerced to the tuple of three `GenerateCaptionVariant`.
 * The result is always padded to exactly 3 elements and the `generationId` always
 * resolves to a valid string (server-supplied or client-generated UUID).
 */
function normalizeCaptionResponse(
  raw: Record<string, unknown>,
  fallbackPlatform: CaptionPlatform
): GenerateCaptionPerPlatformResponse {
  const rawCaptions = (raw.captions ?? []) as unknown;

  const inputArray: unknown[] = Array.isArray(rawCaptions)
    ? rawCaptions
    : Object.values(rawCaptions as Record<string, unknown>);

  const variants: GenerateCaptionVariant[] = inputArray.map((c) => {
    if (typeof c === "string") {
      return { body: c, hashtags: [], charCount: c.length };
    }
    const obj = c as Partial<GenerateCaptionVariant>;
    return {
      body: obj.body ?? "",
      hashtags: obj.hashtags ?? [],
      charCount: obj.charCount ?? 0,
    };
  });

  while (variants.length < 3) {
    variants.push({ body: "", hashtags: [], charCount: 0 });
  }

  return {
    platform:
      (raw.platform as string | undefined as CaptionPlatform | undefined) ??
      fallbackPlatform,
    captions: variants.slice(0, 3) as [
      GenerateCaptionVariant,
      GenerateCaptionVariant,
      GenerateCaptionVariant,
    ],
    generationId:
      (raw.generationId as string | undefined) ?? crypto.randomUUID(),
  };
}

// ─────────────────────────────────────────────────────────────────────────────

function createPartnerPostsModule(
  config: ApiClientConfig,
  request: ReturnType<typeof buildCoreHttp>["request"],
  requestRaw: ReturnType<typeof buildCoreHttp>["requestRaw"]
): PartnerPostsModule {
  const f = config.fetch ?? globalThis.fetch;
  const { baseUrl, clientHeader, storage } = config;

  return {
    list(params?) {
      const qs = params
        ? "?" +
          new URLSearchParams(
            Object.fromEntries(
              Object.entries(params).map(([k, v]) => [k, String(v)])
            )
          ).toString()
        : "";
      return request<PartnerPostsListResponse>(
        "GET",
        `/api/partner/posts${qs}`
      );
    },

    async listPending() {
      const data = await request<{ posts: PartnerPost[] } | PartnerPost[]>(
        "GET",
        "/api/partner/posts/pending"
      );
      return Array.isArray(data)
        ? data
        : ((data as { posts: PartnerPost[] }).posts ?? []);
    },

    async listScheduled() {
      const data = await request<{ posts: PartnerPost[] } | PartnerPost[]>(
        "GET",
        "/api/partner/posts/scheduled"
      );
      return Array.isArray(data)
        ? data
        : ((data as { posts: PartnerPost[] }).posts ?? []);
    },

    create(body) {
      return request<PartnerPostCreateResponse>(
        "POST",
        "/api/partner/posts",
        body
      );
    },

    update(postId, body) {
      return request<PartnerPost>(
        "PATCH",
        `/api/partner/posts/${postId}`,
        body
      );
    },

    async delete(postId) {
      return request<{ success: boolean; wasScheduled: boolean }>(
        "DELETE",
        `/api/partner/posts/${postId}`
      );
    },

    approve(postId) {
      return request<PartnerPostApproveResponse>(
        "POST",
        `/api/partner/posts/${encodeURIComponent(String(postId))}/approve`,
        {}
      );
    },

    reject(postId, note) {
      return request<PartnerPostRejectResponse>(
        "POST",
        `/api/partner/posts/${encodeURIComponent(String(postId))}/reject`,
        { note }
      );
    },

    retrySocial(postId) {
      return request<Record<string, unknown>>(
        "POST",
        `/api/partner/posts/${postId}/social/retry`,
        {}
      );
    },
    retryLinkDelivery(postId, platform, operationId) {
      return request<{ queued: boolean; linkDelivery: LinkDeliveryStatus }>(
        "POST",
        `/api/partner/posts/${encodeURIComponent(
          String(postId)
        )}/link-delivery/retry`,
        {
          ...(platform && { platform }),
          ...(operationId && { operationId }),
        }
      );
    },

    async getSocialStatus(postId) {
      const data = await request<
        SocialPostStatus[] | { posts: SocialPostStatus[] }
      >(
        "GET",
        `/api/partner/social/posts/${encodeURIComponent(String(postId))}`
      );
      if (Array.isArray(data)) return data;
      return (data as { posts?: SocialPostStatus[] }).posts ?? [];
    },

    /**
     * Upload media files to object storage. Uses a direct fetch (not
     * `request`) because `request` serialises the body as JSON and cannot
     * handle FormData. We deliberately do NOT abort this fetch on app
     * background — it must survive backgrounding so a partner who locks
     * the phone mid-upload still completes the publish on return.
     *
     * `options.onProgress` may be passed to receive 0–100 upload-progress updates.
     * `options.signal` may be passed to allow the caller to abort the upload
     * (e.g. when the user taps Cancel in DishCameraModal). The promise
     * rejects with an AbortError when the signal fires.
     *
     * `opts.onProgress` fires on every XHR upload progress tick with an
     * integer 0–100 percentage. When supplied (or when signal is provided)
     * the request is routed through XMLHttpRequest so the runtime can
     * surface byte-level progress events. Callers that omit both options
     * continue to use the simpler fetch path.
     */
    async uploadMedia(
      formData: FormData,
      options?: {
        onProgress?: (
          percent: number,
          bytesPerSecond: number,
          totalExpected: number
        ) => void;
        /**
         * Called at the start of each retry attempt (1-based) before the
         * back-off delay begins. The caller can use this to reset its progress
         * UI to an indeterminate state so the partner sees a clear "retrying"
         * signal instead of a frozen bar at the failure point.
         */
        onRetry?: (attempt: number) => void;
        signal?: AbortSignal;
        /**
         * Task #2706 — optional flow correlation id sent as `X-Request-Id`.
         * Pass the SAME id to the subsequent `linkPhoto` call so the upload and
         * the save share one id across the server's `[UploadCDN]` and
         * `[Partner] link-photo.recv` log lines (the upload→save bridge when
         * diagnosing a rejected photo host).
         */
        requestId?: string;
      }
    ): Promise<MediaUploadResponse> {
      const kudozToken = await storage.getKudozToken();
      const headers: Record<string, string> = { "X-Client": clientHeader };
      if (kudozToken) headers["Authorization"] = `Bearer ${kudozToken}`;
      if (options?.requestId) headers["X-Request-Id"] = options.requestId;

      const url = `${baseUrl}/api/media/upload`;
      const onProgress = options?.onProgress;
      const signal = options?.signal;

      /**
       * Single-attempt upload — called by the retry loop below.
       *
       * When the caller wants upload progress OR an abort signal, drive the
       * request through XMLHttpRequest — fetch() does not expose request-body
       * progress in either browsers or React Native, and the XHR path is also
       * where xhr.abort() lives.  The fetch path stays for callers that don't
       * pass either option (e.g. photo uploads from the camera).
       */
      const doUploadOnce = (): Promise<MediaUploadResponse> => {
        const xhrCtor: typeof XMLHttpRequest | undefined =
          typeof XMLHttpRequest !== "undefined" ? XMLHttpRequest : undefined;
        if ((onProgress || signal) && xhrCtor) {
          if (signal?.aborted) {
            return Promise.reject(
              new ApiError("Upload cancelled", 0, "upload_aborted")
            );
          }
          return new Promise<MediaUploadResponse>((resolve, reject) => {
            const xhr = new xhrCtor();
            xhr.open("POST", url, true);
            xhr.withCredentials = true;
            for (const [k, v] of Object.entries(headers)) {
              xhr.setRequestHeader(k, v);
            }
            let aborted = false;
            const onAbortSignal = () => {
              aborted = true;
              try {
                xhr.abort();
              } catch {
                /* no-op */
              }
              reject(new ApiError("Upload cancelled", 0, "upload_aborted"));
            };
            if (signal) signal.addEventListener("abort", onAbortSignal);
            const cleanup = () => {
              if (signal) signal.removeEventListener("abort", onAbortSignal);
            };
            const RATE_WINDOW_MS = 5_000;
            const rateHistory: Array<{ ts: number; sent: number }> = [];
            let sawComputableProgress = false;
            xhr.upload.onprogress = (ev: ProgressEvent) => {
              if (ev.lengthComputable && ev.total > 0) {
                sawComputableProgress = true;
                const pct = Math.min(
                  100,
                  Math.max(0, Math.round((ev.loaded / ev.total) * 100))
                );
                const now = Date.now();
                rateHistory.push({ ts: now, sent: ev.loaded });
                const cutoff = now - RATE_WINDOW_MS;
                while (rateHistory.length > 1 && rateHistory[0].ts < cutoff) {
                  rateHistory.shift();
                }
                let bytesPerSecond = 0;
                if (rateHistory.length >= 2) {
                  const oldest = rateHistory[0];
                  const newest = rateHistory[rateHistory.length - 1];
                  const deltaBytes = newest.sent - oldest.sent;
                  const deltaMs = newest.ts - oldest.ts;
                  bytesPerSecond =
                    deltaMs > 0 ? (deltaBytes / deltaMs) * 1000 : 0;
                }
                if (onProgress) onProgress(pct, bytesPerSecond, ev.total);
              }
            };
            xhr.upload.onload = () => {
              if (sawComputableProgress && onProgress) onProgress(100, 0, 0);
            };
            xhr.onabort = () => {
              cleanup();
              if (!aborted) {
                reject(new ApiError("Upload cancelled", 0, "upload_aborted"));
              }
            };
            xhr.onerror = () => {
              cleanup();
              if (aborted) return;
              reject(new ApiError("Network error during upload", 0));
            };
            xhr.ontimeout = () => {
              cleanup();
              if (aborted) return;
              reject(new ApiError("Upload timed out", 0));
            };
            xhr.onload = () => {
              cleanup();
              if (aborted) return;
              const status = xhr.status;
              const raw = xhr.responseText || "";
              let parsed: Record<string, unknown>;
              try {
                parsed = raw
                  ? (JSON.parse(raw) as Record<string, unknown>)
                  : {};
              } catch {
                parsed = {};
              }
              if (status >= 200 && status < 300) {
                resolve(parsed as unknown as MediaUploadResponse);
                return;
              }
              const message =
                (parsed.error as string) || xhr.statusText || String(status);
              const code = parsed.code as string | undefined;
              reject(
                new ApiError(
                  buildApiErrorMessage(status, message),
                  status,
                  code,
                  parsed
                )
              );
            };
            xhr.send(formData as unknown as XMLHttpRequestBodyInit);
          });
        }

        return (async () => {
          const res = await f(url, {
            method: "POST",
            headers,
            body: formData,
            credentials: "include",
            signal: signal ?? null,
          });

          if (!res.ok) {
            const body = (await res.json().catch(() => ({}))) as Record<
              string,
              unknown
            >;
            const message =
              (body.error as string) || res.statusText || String(res.status);
            const code = body.code as string | undefined;
            throw new ApiError(
              buildApiErrorMessage(res.status, message),
              res.status,
              code,
              body
            );
          }

          return res.json() as Promise<MediaUploadResponse>;
        })();
      };

      // Bounded retry on transient 502/503/504 and network failures.
      // Same back-off schedule as the video-master upload (1 s → 3 s → 6 s,
      // 3 retries max). Respects the caller's AbortSignal so a partner cancel
      // still stops immediately. A retry that succeeds is transparent to the
      // caller — no error surfaces unless all attempts are exhausted.
      const onRetry = options?.onRetry;
      let lastErr: unknown;
      for (
        let attempt = 0;
        attempt <= MEDIA_UPLOAD_RETRY_DELAYS_MS.length;
        attempt++
      ) {
        if (attempt > 0) {
          // Notify the caller that a retry is about to begin (before the
          // back-off delay) so it can switch its progress UI to an indeterminate
          // state immediately — the partner sees a clear "retrying" signal
          // rather than a frozen bar stuck at the failure point.
          if (onRetry) onRetry(attempt);
          const delayMs = MEDIA_UPLOAD_RETRY_DELAYS_MS[attempt - 1];
          await new Promise<void>((resolve) => {
            if (signal?.aborted) {
              resolve();
              return;
            }
            const timer = setTimeout(resolve, delayMs);
            if (signal) {
              signal.addEventListener(
                "abort",
                () => {
                  clearTimeout(timer);
                  resolve();
                },
                { once: true }
              );
            }
          });
          if (signal?.aborted) break;
          // Reset the progress bar so the partner sees a fresh 0 → 100 sweep
          // on the retry attempt rather than a frozen bar at the failure point.
          if (onProgress) onProgress(0, 0, 0);
        }
        try {
          return await doUploadOnce();
        } catch (err) {
          lastErr = err;
          if (!isRetryableMediaUploadError(err) || signal?.aborted) break;
        }
      }
      throw lastErr;
    },

    generateCaption(body) {
      return request<GenerateCaptionResponse>(
        "POST",
        "/api/partner/posts/generate-caption",
        body
      );
    },

    async generateCaptionPerPlatform(body) {
      // Strip the client-side `platform` hint from the wire payload.
      // Map it to `targetPlatforms` so the server applies per-platform
      // prompt tuning (TikTok hook format, IG/FB character limits, etc.)
      // unless the caller already supplied `targetPlatforms` explicitly.
      const { platform, ...rest } = body;
      const wireBody: Record<string, unknown> = {
        ...rest,
        targetPlatforms: body.targetPlatforms ?? [platform],
      };
      const raw = await request<Record<string, unknown>>(
        "POST",
        "/api/partner/posts/generate-caption",
        wireBody
      );
      return normalizeCaptionResponse(raw, platform);
    },

    polishCaption(body) {
      return request<PolishCaptionResponse>(
        "POST",
        "/api/partner/posts/polish-caption",
        body
      );
    },

    async generateCaptionStreaming(body, onEvent, signal) {
      // Strip the client-side `platform` hint from the wire payload and map
      // it to `targetPlatforms` — same transformation as generateCaptionPerPlatform.
      const { platform, ...rest } = body;
      const wireBody: Record<string, unknown> = {
        ...rest,
        targetPlatforms: body.targetPlatforms ?? [platform],
      };

      // Uses requestRaw so all auth headers, refresh-and-retry, partner headers,
      // and X-Client are applied identically to every other API call — no raw
      // fetch bypass.
      let res: Response;
      try {
        res = await requestRaw(
          "POST",
          "/api/partner/posts/generate-caption",
          wireBody,
          undefined,
          signal
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : "Network error";
        onEvent({ type: "error", message });
        throw err;
      }

      // Task #3854 — an `{"error": "..."}` JSON body arriving with a 200 (or
      // slipping through mid-stream) previously normalized to silently-empty
      // captions. Detect it and throw so callers surface the failure.
      const throwIfErrorPayload = (parsed: Record<string, unknown>): void => {
        if (
          parsed &&
          typeof parsed.error === "string" &&
          !Array.isArray(parsed.captions)
        ) {
          const err = new ApiError(parsed.error, res.status, "CAPTION_ERROR");
          onEvent({ type: "error", message: parsed.error });
          throw err;
        }
      };

      // The server may stream whitespace heartbeat bytes before the final JSON.
      // Read the body incrementally:
      //   whitespace-only chunks → "heartbeat" (responseHeartbeat protocol)
      //   non-whitespace accumulation → "partial"
      //   valid JSON parses → "complete"
      const reader = res.body?.getReader();
      if (!reader) {
        // Fallback: body not streamable (e.g. test environments, some proxies).
        // Parse whole body, run through the same normalization as the streaming path.
        const text = await res.text();
        let wholeParsed: Record<string, unknown>;
        try {
          wholeParsed = JSON.parse(text) as Record<string, unknown>;
        } catch {
          onEvent({ type: "error", message: "Response parse failed" });
          throw new ApiError(
            "Caption response could not be parsed",
            500,
            "PARSE_ERROR"
          );
        }
        throwIfErrorPayload(wholeParsed);
        const data = normalizeCaptionResponse(wholeParsed, platform);
        onEvent({ type: "complete", data });
        return data;
      }

      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        let done: boolean;
        let value: Uint8Array | undefined;
        try {
          ({ done, value } = await reader.read());
        } catch (err) {
          const message =
            err instanceof Error ? err.message : "Stream read error";
          onEvent({ type: "error", message });
          throw err;
        }

        if (done) break;

        const chunk = decoder.decode(value, { stream: true });

        if (chunk.trim() === "") {
          // Heartbeat — whitespace-only filler from responseHeartbeat.
          onEvent({ type: "heartbeat" });
          continue;
        }

        buffer += chunk;
        onEvent({ type: "partial", text: buffer });

        // Attempt an incremental parse so we surface the result as soon as
        // the complete JSON object arrives, even if more chunks follow.
        // The JSON.parse try/catch is deliberately separate from the
        // error-payload check so a thrown ApiError is NOT swallowed by the
        // "not yet valid JSON" catch.
        let incrementalParsed: Record<string, unknown> | null = null;
        try {
          incrementalParsed = JSON.parse(buffer) as Record<string, unknown>;
        } catch {
          // Not yet valid JSON — accumulate and wait for more chunks.
        }
        if (incrementalParsed !== null) {
          reader.cancel().catch(() => {});
          throwIfErrorPayload(incrementalParsed);
          const data = normalizeCaptionResponse(incrementalParsed, platform);
          onEvent({ type: "complete", data });
          return data;
        }
      }

      // Stream ended: attempt a final parse of whatever was accumulated.
      let finalParsed: Record<string, unknown>;
      try {
        finalParsed = JSON.parse(buffer) as Record<string, unknown>;
      } catch {
        onEvent({ type: "error", message: "Incomplete stream response" });
        throw new ApiError(
          "Caption stream ended without valid JSON",
          500,
          "STREAM_TRUNCATED"
        );
      }
      throwIfErrorPayload(finalParsed);
      const data = normalizeCaptionResponse(finalParsed, platform);
      onEvent({ type: "complete", data });
      return data;
    },

    async recordCaptionEvent(generationId, postType, variantIndex, signal) {
      // Wire payload matches server Zod schema exactly:
      //   generationId (8–64 chars), variantIndex (int 0–10),
      //   postType (1–64 chars), eventKind ("chosen" — only accepted value today).
      // signal is forwarded so callers (e.g. component unmount) can cancel the POST.
      await request<void>(
        "POST",
        "/api/partner/posts/caption-events",
        { generationId, postType, variantIndex, eventKind: "chosen" },
        undefined,
        signal
      );
    },

    schedule(postId, scheduledAt) {
      return request<PartnerPost>(
        "PATCH",
        `/api/partner/posts/${postId}/schedule`,
        { scheduledAt }
      );
    },
  };
}

function createPartnerAudioModule(
  request: ReturnType<typeof buildCoreHttp>["request"]
): PartnerAudioModule {
  return {
    list(filter) {
      const params = new URLSearchParams();
      if (filter?.mood) params.set("mood", filter.mood);
      if (filter?.q && filter.q.trim()) params.set("q", filter.q.trim());
      const qs = params.toString();
      return request<PartnerAudioTracksResponse>(
        "GET",
        `/api/partner/audio/tracks${qs ? `?${qs}` : ""}`
      );
    },
    moods() {
      return request<PartnerAudioMoodsResponse>(
        "GET",
        "/api/partner/audio/moods"
      );
    },
    upload(formData) {
      // FormData bodies are detected as binary by the core http layer, so the
      // multipart boundary / Content-Type is set by fetch — not JSON-encoded.
      return request<{ track: PartnerAudioTrack }>(
        "POST",
        "/api/partner/audio/upload",
        formData
      );
    },
    async delete(id) {
      await request<void>(
        "DELETE",
        `/api/partner/audio/tracks/${encodeURIComponent(id)}`
      );
    },
  };
}

function createPartnerMenuModule(
  request: ReturnType<typeof buildCoreHttp>["request"]
): PartnerMenuModule {
  return {
    list(restaurantProfile) {
      return request<PartnerMenuResponse>(
        "GET",
        `/api/partner/menu/${encodeURIComponent(restaurantProfile)}`
      );
    },

    getItem(restaurantId, menuId, itemId) {
      return request<PartnerMenuItemDetail>(
        "GET",
        `/api/partner/restmanager/${encodeURIComponent(String(restaurantId))}/menus/${encodeURIComponent(String(menuId))}/items/${encodeURIComponent(itemId)}`
      );
    },

    createItem(restaurantId, menuId, body) {
      return request<PartnerMenuItemDetail>(
        "POST",
        `/api/partner/restmanager/${encodeURIComponent(String(restaurantId))}/menus/${encodeURIComponent(String(menuId))}/items`,
        body
      );
    },

    updateItem(restaurantId, menuId, itemId, body) {
      return request<PartnerMenuItemDetail>(
        "POST",
        `/api/partner/restmanager/${encodeURIComponent(String(restaurantId))}/menus/${encodeURIComponent(String(menuId))}/items/${encodeURIComponent(itemId)}`,
        body
      );
    },

    async deleteItem(restaurantId, menuId, itemId) {
      await request<void>(
        "DELETE",
        `/api/partner/restmanager/${encodeURIComponent(String(restaurantId))}/menus/${encodeURIComponent(String(menuId))}/items/${encodeURIComponent(itemId)}`
      );
    },

    async deactivateItem(restaurantId, menuId, itemId) {
      await request<void>(
        "DELETE",
        `/api/partner/restmanager/${encodeURIComponent(String(restaurantId))}/menus/${encodeURIComponent(String(menuId))}/items/${encodeURIComponent(itemId)}`
      );
    },

    async restoreItem(restaurantId, menuId, itemId) {
      await request<void>(
        "PUT",
        `/api/partner/restmanager/${encodeURIComponent(String(restaurantId))}/menus/${encodeURIComponent(String(menuId))}/items/${encodeURIComponent(itemId)}/restore`
      );
    },

    linkVideo(restaurantId, menuId, itemId, videoUrl, videoThumbnailUrl) {
      return request<{ success: boolean }>(
        "POST",
        `/api/partner/restmanager/${encodeURIComponent(String(restaurantId))}/menus/${encodeURIComponent(String(menuId))}/items/${encodeURIComponent(itemId)}/link-video`,
        { videoUrl, ...(videoThumbnailUrl ? { videoThumbnailUrl } : {}) }
      );
    },

    unlinkVideo(restaurantId, menuId, itemId) {
      return request<{ success: boolean }>(
        "DELETE",
        `/api/partner/restmanager/${encodeURIComponent(String(restaurantId))}/menus/${encodeURIComponent(String(menuId))}/items/${encodeURIComponent(itemId)}/link-video`
      );
    },

    linkPhoto(restaurantId, menuId, itemId, photoUrl, requestId) {
      return request<{ success: boolean }>(
        "POST",
        `/api/partner/restmanager/${encodeURIComponent(String(restaurantId))}/menus/${encodeURIComponent(String(menuId))}/items/${encodeURIComponent(itemId)}/link-photo`,
        { photoUrl },
        undefined,
        undefined,
        requestId
      );
    },

    unlinkPhoto(restaurantId, menuId, itemId) {
      return request<{ success: boolean }>(
        "DELETE",
        `/api/partner/restmanager/${encodeURIComponent(String(restaurantId))}/menus/${encodeURIComponent(String(menuId))}/items/${encodeURIComponent(itemId)}/link-photo`
      );
    },
  };
}

/**
 * Raw row shape returned by the server's `/api/partner/restmanager/restaurants`
 * endpoint: the Eatzy Restaurant Manager fields enriched with `kudozUuid`
 * (the local Kudoz restaurant id, or null) and `isActive`.
 */
interface RestmanagerRestaurantRow {
  id: number;
  profile?: string | null;
  name: string;
  address?: string | null;
  timezone?: string | null;
  services?: { delivery?: { logo?: string | null } | null } | null;
  kudozUuid?: string | null;
  isActive?: boolean;
  /**
   * The restaurant's logo URL, resolved server-side via a three-tier fallback:
   *   1. Local DB `restaurants.logoImageUrl` (populated by Eatzy restaurant sync)
   *   2. Kudoz consumer-API `logo_url` (same source as the sync; reliable fallback)
   *   3. Eatzy Manager API `services.delivery.logo` (generic platform icon, last resort)
   * Each tier is validated as an absolute https:// URL server-side, so profile
   * slugs stored by partnerAuth.ts can never slip through as a broken image.
   */
  logoImageUrl?: string | null;
}

function mapRestmanagerRestaurant(
  row: RestmanagerRestaurantRow
): PartnerRestaurant {
  return {
    // Prefer the Kudoz UUID so linked restaurants keep their existing id; fall
    // back to the stringified Eatzy id for restaurants without a local record
    // (these are auto-provisioned server-side via the x-restaurant-id header).
    id: row.kudozUuid ?? String(row.id),
    name: row.name,
    address: row.address ?? null,
    // Restaurant Manager does not return a separate city field.
    city: null,
    // Logo is resolved server-side via a three-tier fallback (see above).
    // Retain the services?.delivery?.logo client-side guard as belt-and-suspenders
    // for any older server builds that haven't deployed the three-tier logic yet.
    logoImageUrl: row.logoImageUrl ?? row.services?.delivery?.logo ?? null,
    // Restaurant Manager does not return a per-restaurant role. Default to the
    // least-privileged "staff" so the capability fallback never over-grants UI
    // affordances; the authoritative role for the active restaurant is fetched
    // from GET /api/partner/me (which also drives useCapabilities).
    partnerRole: "staff",
    eatzyClientId: row.profile ?? null,
    kudozExternalId: typeof row.id === "number" ? row.id : null,
    eatzyTimezone: row.timezone ?? null,
    isActive: row.isActive !== false,
  };
}

function createPartnerRestaurantsModule(
  request: ReturnType<typeof buildCoreHttp>["request"]
): PartnerRestaurantsModule {
  return {
    async list() {
      // Source of truth is the Eatzy Restaurant Manager set (matches the web
      // Partner Portal), NOT the local-DB-only `/api/partner/restaurants`. The
      // x-eatzy-token required by this endpoint is attached automatically by
      // the partner headers provider.
      const data = await request<
        RestmanagerRestaurantRow[] | { restaurants: RestmanagerRestaurantRow[] }
      >("GET", "/api/partner/restmanager/restaurants");
      const rows = Array.isArray(data)
        ? data
        : ((data as { restaurants: RestmanagerRestaurantRow[] }).restaurants ??
          []);
      return rows.map(mapRestmanagerRestaurant);
    },
  };
}

function createPartnerNotificationsModule(
  request: ReturnType<typeof buildCoreHttp>["request"]
): PartnerNotificationsModule {
  return {
    async list() {
      const data = await request<
        PartnerNotification[] | { notifications: PartnerNotification[] }
      >("GET", "/api/partner/notifications");
      return Array.isArray(data)
        ? data
        : ((data as { notifications: PartnerNotification[] }).notifications ??
            []);
    },

    async markRead(id) {
      await request<void>("POST", `/api/partner/notifications/${id}/read`, {});
    },

    async markAllRead() {
      await request<void>("POST", "/api/partner/notifications/read-all", {});
    },

    async dismiss(id) {
      await request<void>(
        "POST",
        `/api/partner/notifications/${id}/dismiss`,
        {}
      );
    },
  };
}

function createPartnerDashboardModule(
  request: ReturnType<typeof buildCoreHttp>["request"]
): PartnerDashboardModule {
  return {
    stats() {
      return request<PartnerDashboardStats>(
        "GET",
        "/api/partner/dashboard/stats"
      );
    },
  };
}

function createPartnerSocialModule(
  request: ReturnType<typeof buildCoreHttp>["request"]
): PartnerSocialModule {
  return {
    startOAuth(platform, restaurantId, returnTo) {
      const params = new URLSearchParams({
        restaurantId: String(restaurantId),
      });
      if (returnTo) params.set("returnTo", returnTo);
      return request<{ url: string }>(
        "GET",
        `/api/partner/social/oauth/${encodeURIComponent(platform)}/start?${params.toString()}`
      );
    },
    getOAuthStatus(restaurantId) {
      return request(
        "GET",
        `/api/partner/social/oauth/status?restaurantId=${encodeURIComponent(String(restaurantId))}`
      );
    },
    getConnections(restaurantId) {
      return request<SocialConnection[]>(
        "GET",
        `/api/partner/social/connections?restaurantId=${encodeURIComponent(String(restaurantId))}`
      );
    },
    getPerformance(restaurantId, period = 30) {
      const params = new URLSearchParams({
        restaurantId: String(restaurantId),
        period: String(period),
      });
      return request<QuickPostPerformanceReport>(
        "GET",
        `/api/partner/social/performance?${params.toString()}`
      );
    },
  };
}

function createAccountModule(
  request: ReturnType<typeof buildCoreHttp>["request"]
): AccountModule {
  async function requestDeletion(
    email: string
  ): Promise<AccountDeletionResponse> {
    return request<AccountDeletionResponse>("POST", "/api/account/delete", {
      email,
    });
  }

  return {
    async delete(email) {
      const res = await requestDeletion(email);
      return {
        deletionScheduledAt: (res.deletionScheduledAt as string) ?? "",
        gracePeriodDays: res.gracePeriodDays ?? 30,
      };
    },

    requestDeletion,

    async cancelDeletion() {
      return request<AccountCancelDeletionResponse>(
        "POST",
        "/api/account/delete/cancel",
        {}
      );
    },

    async cancelDelete() {
      await request<void>("POST", "/api/account/delete/cancel", {});
    },

    async getDeletionStatus() {
      return request<AccountDeletionStatus>(
        "GET",
        "/api/account/deletion-status"
      );
    },
  };
}

function createMediaModule(
  config: ApiClientConfig,
  _request: ReturnType<typeof buildCoreHttp>["request"]
): MediaModule {
  const f = config.fetch ?? globalThis.fetch;
  const { baseUrl, clientHeader, storage } = config;

  async function uploadOne(
    file: File | Blob,
    type: "photo" | "video",
    name: string
  ): Promise<MediaUploadResponse> {
    const kudozToken = await storage.getKudozToken();
    const headers: Record<string, string> = { "X-Client": clientHeader };
    if (kudozToken) headers["Authorization"] = `Bearer ${kudozToken}`;

    const formData = new FormData();
    formData.append("files", file, name);
    formData.append("type", type);

    const res = await f(`${baseUrl}/api/media/upload`, {
      method: "POST",
      headers,
      body: formData,
      credentials: "include",
    });

    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as Record<
        string,
        unknown
      >;
      const message =
        (body.error as string) || res.statusText || String(res.status);
      const code = body.code as string | undefined;
      throw new ApiError(
        buildApiErrorMessage(res.status, message),
        res.status,
        code,
        body
      );
    }

    return res.json() as Promise<MediaUploadResponse>;
  }

  return {
    async upload(file, type, name = "upload") {
      return uploadOne(file, type, name);
    },

    async uploadBatch(files, type, onProgress) {
      // Enforce the IG-style carousel cap (decision #18 — max 10 images per post).
      if (files.length > 10) {
        throw new Error(
          `uploadBatch: at most 10 files may be uploaded per call (received ${files.length})`
        );
      }

      const total = files.length;
      const succeeded: MediaUploadBatchResult["succeeded"] = [];
      const failed: MediaUploadBatchResult["failed"] = [];

      for (let i = 0; i < total; i++) {
        const { file, name = `upload-${i}` } = files[i];
        try {
          const result = await uploadOne(file, type, name);
          // The server returns one `files` entry per uploaded file; take index 0
          // since we send exactly one file per request.
          const entry = result.files[0];
          if (!entry) {
            throw new Error("upload response missing file entry");
          }
          succeeded.push({
            url: entry.url,
            type: entry.type,
            originalName: entry.originalName,
            // Use the caller's position in the batch, not the server's index,
            // so the UI can map results back to the original file list.
            index: i,
          });
        } catch (err) {
          failed.push({
            index: i,
            name,
            error: err instanceof Error ? err.message : String(err),
          });
        }
        onProgress?.(i + 1, total);
      }

      return { succeeded, failed };
    },
  };
}

function createPartnerVideoModule(
  config: ApiClientConfig,
  request: ReturnType<typeof buildCoreHttp>["request"]
): PartnerVideoModule {
  const f = config.fetch ?? globalThis.fetch;
  const { baseUrl, clientHeader, storage } = config;

  return {
    /**
     * Uploads the raw master clip as multipart/form-data.
     * Uses a direct fetch (not `request`) because `request` serialises the
     * body as JSON and cannot handle FormData.
     */
    async uploadMaster(
      formData: FormData,
      options?: UploadMasterOptions
    ): Promise<VideoUploadResponse> {
      const kudozToken = await storage.getKudozToken();
      // Client-generated correlation id (Task #2674 pattern). Sent as
      // X-Request-Id so the server's "video/upload" log line can be matched
      // 1:1 with the on-device breadcrumb — the only bridge available without
      // production device access. Also surfaced on any thrown ApiError so the
      // caller (videoPipelineRunner) can record it in a Bugsnag breadcrumb.
      const requestId = makeRequestId();
      const headers: Record<string, string> = {
        "X-Client": clientHeader,
        "X-Request-Id": requestId,
      };
      if (kudozToken) headers["Authorization"] = `Bearer ${kudozToken}`;
      // Attach the partner context headers (x-restaurant-id, x-eatzy-token) that
      // the canonical `request` wrapper injects for every other /api/partner/
      // call. `uploadMaster` sends via raw XHR/fetch and bypasses that wrapper,
      // so without these the server's `requirePartner` auth cannot resolve or
      // authorize the restaurant for the multipart upload — it reads
      // `x-restaurant-id`, not the multipart body — which surfaced as a 403
      // "Not authorized for this restaurant". Covers both the XHR and fetch
      // paths below since both send this same `headers` object.
      //
      // Merge without clobbering: the headers already set above (Authorization,
      // X-Client, X-Request-Id) take precedence, so a future provider that
      // accidentally returns one of those keys can never overwrite the correct
      // auth token or break request-id correlation.
      if (config.partnerHeadersProvider) {
        const reserved = new Set(
          Object.keys(headers).map((k) => k.toLowerCase())
        );
        for (const [k, v] of Object.entries(config.partnerHeadersProvider())) {
          if (reserved.has(k.toLowerCase())) continue;
          headers[k] = v;
        }
      }

      const url = `${baseUrl}/api/partner/video/upload`;

      const signal = options?.signal;

      // Feed this upload into the same diagnostics Network Trail the `request`
      // wrapper uses (Task #2644). `uploadMaster` sends via raw XHR/fetch and
      // bypasses that wrapper, so without this the upload leg never shows up in
      // a device diagnostics bundle — the exact gap that made the video-upload
      // 403 impossible to place (edge vs origin). Body-free + token-free, and
      // never allowed to throw back into the upload path.
      const startedAt = Date.now();
      const emitUploadObserve = (
        status: number,
        errorCode: string | null,
        respRequestId: string | null
      ): void => {
        const observe = config.requestObserver;
        if (!observe) return;
        try {
          observe({
            method: "POST",
            path: "/api/partner/video/upload",
            status,
            durationMs: Date.now() - startedAt,
            requestId: respRequestId ?? requestId,
            errorCode,
          });
        } catch {
          // never let diagnostics observation throw into the upload path
        }
      };

      // Honour an already-aborted signal before we open any sockets — saves
      // a network round-trip when the partner cancels before the upload
      // promise is awaited. Still record a single Network-Trail breadcrumb so
      // every upload outcome (including a pre-abort) is traceable on-device.
      if (signal?.aborted) {
        emitUploadObserve(0, "upload_aborted", null);
        throw new ApiError("Upload cancelled", 0, "upload_aborted");
      }

      // When the caller wants upload progress OR an abort signal, drive
      // the request through XMLHttpRequest — fetch() does not expose
      // request-body progress in either browsers or React Native, and
      // the XHR path is also where `xhr.abort()` lives. The fetch path
      // stays for callers that don't pass either option.
      const xhrCtor: typeof XMLHttpRequest | undefined =
        typeof XMLHttpRequest !== "undefined" ? XMLHttpRequest : undefined;
      if ((options?.onProgress || signal) && xhrCtor) {
        const onProgress = options?.onProgress;
        return await new Promise<VideoUploadResponse>((resolve, reject) => {
          const xhr = new xhrCtor();
          xhr.open("POST", url, true);
          xhr.withCredentials = true;
          for (const [k, v] of Object.entries(headers)) {
            xhr.setRequestHeader(k, v);
          }
          let aborted = false;
          const onAbortSignal = () => {
            aborted = true;
            try {
              xhr.abort();
            } catch {
              /* no-op */
            }
            emitUploadObserve(0, "upload_aborted", null);
            reject(new ApiError("Upload cancelled", 0, "upload_aborted"));
          };
          if (signal) {
            signal.addEventListener("abort", onAbortSignal);
          }
          const cleanup = () => {
            if (signal) signal.removeEventListener("abort", onAbortSignal);
          };
          let sawComputableProgress = false;
          xhr.upload.onprogress = (ev: ProgressEvent) => {
            if (ev.lengthComputable && ev.total > 0) {
              sawComputableProgress = true;
              const percent = Math.min(
                100,
                Math.max(0, Math.round((ev.loaded / ev.total) * 100))
              );
              if (onProgress) onProgress(percent);
            }
          };
          xhr.upload.onload = () => {
            if (sawComputableProgress && onProgress) onProgress(100);
          };
          xhr.onabort = () => {
            cleanup();
            if (!aborted) {
              emitUploadObserve(0, "upload_aborted", null);
              reject(new ApiError("Upload cancelled", 0, "upload_aborted"));
            }
          };
          xhr.onerror = () => {
            cleanup();
            if (aborted) return;
            emitUploadObserve(0, "network_error", null);
            reject(new ApiError("Network error during upload", 0));
          };
          xhr.ontimeout = () => {
            cleanup();
            if (aborted) return;
            emitUploadObserve(0, "timeout", null);
            reject(new ApiError("Upload timed out", 0));
          };
          xhr.onload = () => {
            cleanup();
            if (aborted) return;
            const status = xhr.status;
            const raw = xhr.responseText || "";
            const respRequestId = readXhrHeader(xhr, "x-request-id");
            let parsed: Record<string, unknown>;
            try {
              parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
            } catch {
              parsed = {};
            }
            if (status >= 200 && status < 300) {
              emitUploadObserve(status, null, respRequestId);
              resolve(parsed as unknown as VideoUploadResponse);
              return;
            }
            // A Cloudflare Managed Challenge / Browser Integrity Check
            // 403/503/429 can arrive as challenge HTML OR as an empty body with
            // a `cf-mitigated` header (no JSON `error`), so without this branch
            // the partner only sees a bare "403:". Detect it (body + headers),
            // surface a readable message, and tag it with a `cf_challenge` code
            // — plus the `cf-ray` id — so diagnostics distinguish an edge block
            // from a genuine server auth 403. The real fix is the WAF Skip rule
            // for /api/partner/video/upload (see auth.ts operator note).
            const cfRay = readXhrHeader(xhr, "cf-ray");
            const cfMitigated = readXhrHeader(xhr, "cf-mitigated");
            if (isCloudflareChallenge(status, raw, { cfRay, cfMitigated })) {
              emitUploadObserve(status, "cf_challenge", respRequestId);
              reject(
                new ApiError(
                  "QuickPost servers are busy right now. Please try again in a moment — if it keeps happening, contact support.",
                  status,
                  "cf_challenge",
                  { requestId, cfRay }
                )
              );
              return;
            }
            const message =
              (parsed.error as string) || xhr.statusText || String(status);
            const code = parsed.code as string | undefined;
            emitUploadObserve(status, code ?? `http_${status}`, respRequestId);
            reject(
              new ApiError(
                buildApiErrorMessage(status, message),
                status,
                code,
                {
                  ...parsed,
                  requestId,
                  cfRay,
                }
              )
            );
          };
          xhr.send(formData as unknown as XMLHttpRequestBodyInit);
        });
      }

      let res: Response;
      try {
        res = await f(url, {
          method: "POST",
          headers,
          body: formData,
          credentials: "include",
          signal: signal ?? null,
        });
      } catch (err) {
        // Transport failure (DNS, connection reset) or an aborted fetch — there
        // is no Response, so record a status-0 breadcrumb and rethrow the
        // original error unchanged (callers already special-case AbortError).
        const aborted = (err as { name?: string })?.name === "AbortError";
        emitUploadObserve(
          0,
          aborted ? "upload_aborted" : "network_error",
          null
        );
        throw err;
      }

      const respRequestId = readResHeader(res, "x-request-id");

      if (!res.ok) {
        // Read the raw text first: a Cloudflare challenge returns HTML, so
        // res.json() would throw it away and we could not classify it.
        const raw = await res.text().catch(() => "");
        let body: Record<string, unknown>;
        try {
          body = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
        } catch {
          body = {};
        }
        // Body markers OR the `cf-mitigated` header (empty-body challenge);
        // `cf-ray` is captured for diagnostics/correlation only.
        const cfRay = readResHeader(res, "cf-ray");
        const cfMitigated = readResHeader(res, "cf-mitigated");
        if (isCloudflareChallenge(res.status, raw, { cfRay, cfMitigated })) {
          emitUploadObserve(res.status, "cf_challenge", respRequestId);
          throw new ApiError(
            "QuickPost servers are busy right now. Please try again in a moment — if it keeps happening, contact support.",
            res.status,
            "cf_challenge",
            { requestId, cfRay }
          );
        }
        const message =
          (body.error as string) || res.statusText || String(res.status);
        const code = body.code as string | undefined;
        emitUploadObserve(
          res.status,
          code ?? `http_${res.status}`,
          respRequestId
        );
        throw new ApiError(
          buildApiErrorMessage(res.status, message),
          res.status,
          code,
          {
            ...body,
            requestId,
            cfRay,
          }
        );
      }

      emitUploadObserve(res.status, null, respRequestId);
      return res.json() as Promise<VideoUploadResponse>;
    },

    transcode(body: VideoTranscodeBody): Promise<VideoTranscodeAck> {
      return request<VideoTranscodeAck>(
        "POST",
        "/api/partner/video/transcode",
        body
      );
    },

    reencodeForPlatforms(
      jobId: string,
      body: VideoReencodeBody
    ): Promise<VideoReencodeResponse> {
      return request<VideoReencodeResponse>(
        "POST",
        `/api/partner/video/${encodeURIComponent(jobId)}/reencode`,
        body
      );
    },

    async getStatus(jobId: string): Promise<VideoTranscodeStatus | null> {
      // Server returns a status envelope:
      //   200 { status: 'done', jobId, result }    → return { kind: 'done', result }
      //   200 { status: 'processing'|'pending', jobId, completedEncodings,
      //         totalEncodings, progressStep }
      //         → return { kind: 'progress', progress } (Task #1085, #1120)
      //   200 { status: 'failed', jobId, errorMessage, errorCode }
      //         → return { kind: 'failed', errorMessage, errorCode } (Task #1228)
      //         Callers must stop polling and surface the error immediately —
      //         returning rather than throwing lets the poll loop distinguish a
      //         permanent server-side job failure from a transient network error.
      //   404 { error, code: 'JOB_NOT_FOUND' | 'JOB_EXPIRED' } → return null
      try {
        const envelope = await request<{
          status: "pending" | "processing" | "done" | "failed";
          jobId: string;
          result?: VideoTranscodeResult;
          completedEncodings?: number;
          totalEncodings?: number | null;
          progressStep?: string | null;
          errorMessage?: string;
          error?: string;
          errorCode?: string;
          code?: string;
        }>("GET", `/api/partner/video/${encodeURIComponent(jobId)}/status`);
        if (envelope.status === "done" && envelope.result) {
          return { kind: "done", result: envelope.result };
        }
        if (envelope.status === "failed") {
          return {
            kind: "failed",
            errorMessage:
              envelope.errorMessage ??
              envelope.error ??
              "Transcoding failed. Please try again.",
            errorCode:
              envelope.errorCode ?? envelope.code ?? "TRANSCODE_FAILED",
          };
        }
        // pending / processing — surface progress (Task #1085, #1120). Old
        // job rows without the columns send neither field; pass `null` for
        // `totalEncodings` and `progressStep` so callers fall back gracefully.
        return {
          kind: "progress",
          progress: {
            completedEncodings: envelope.completedEncodings ?? 0,
            totalEncodings:
              typeof envelope.totalEncodings === "number"
                ? envelope.totalEncodings
                : null,
            progressStep: envelope.progressStep ?? null,
          },
        };
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
  };
}

function createShortLinksModule(
  request: ReturnType<typeof buildCoreHttp>["request"]
): ShortLinksModule {
  return {
    async create(body) {
      const data = await request<{
        code?: string;
        shortUrl?: string;
        url?: string;
        longUrl?: string;
      }>("POST", "/api/partner/links/shorten", {
        url: body.url,
        ...(body.label ? { label: body.label } : {}),
        ...(body.suggestedCode ? { suggestedCode: body.suggestedCode } : {}),
      });

      // Hard contract: the server MUST return both `code` and `shortUrl`.
      // Silent fallbacks here would silently embed the long URL in every
      // caption, defeating the purpose of the short-link system.
      if (!data.code) {
        throw new ApiError(
          "shortLinks.create: server response is missing `code`",
          500,
          "MISSING_SHORT_LINK_CODE"
        );
      }
      if (!data.shortUrl) {
        throw new ApiError(
          "shortLinks.create: server response is missing `shortUrl`",
          500,
          "MISSING_SHORT_URL"
        );
      }

      return {
        code: data.code,
        shortUrl: data.shortUrl,
        longUrl: data.longUrl ?? data.url ?? body.url,
      };
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Mobile publish module factory (Task #1009)
// ─────────────────────────────────────────────────────────────────────────────

function createMobilePublishModule(
  request: <T>(
    method: string,
    path: string,
    body?: unknown,
    extraHeaders?: Record<string, string>
  ) => Promise<T>
): MobilePublishModule {
  return {
    async startUpload(body) {
      return request<MobileUploadStartResponse>(
        "POST",
        "/api/partner/mobile/upload/start",
        body
      );
    },

    async uploadChunk(sessionId, chunkIndex, chunkData) {
      const buf =
        chunkData instanceof ArrayBuffer ? chunkData : chunkData.buffer;
      return request<MobileUploadChunkResponse>(
        "POST",
        `/api/partner/mobile/upload/${encodeURIComponent(sessionId)}/chunk?index=${chunkIndex}`,
        buf,
        { "Content-Type": "application/octet-stream" }
      );
    },

    async completeUpload(sessionId) {
      return request<MobileUploadCompleteResponse>(
        "POST",
        `/api/partner/mobile/upload/${encodeURIComponent(sessionId)}/complete`
      );
    },

    async abortUpload(sessionId) {
      await request<void>(
        "POST",
        `/api/partner/mobile/upload/${encodeURIComponent(sessionId)}/abort`
      );
    },

    async publish(body) {
      return request<MobilePublishResponse>(
        "POST",
        "/api/partner/mobile/posts",
        body
      );
    },

    async getStatus(postId) {
      return request<MobilePostStatusResponse>(
        "GET",
        `/api/partner/mobile/posts/${encodeURIComponent(postId)}/status`
      );
    },

    async retryPlatform(postId, platform) {
      return request<{
        queued: boolean;
        partnerPostId: string;
        platform: string;
      }>(
        "POST",
        `/api/partner/mobile/posts/${encodeURIComponent(postId)}/retry/${encodeURIComponent(platform)}`
      );
    },

    async todaysFeed(date?: string, tz?: string) {
      const params = new URLSearchParams();
      if (date) params.set("date", date);
      if (tz) params.set("tz", tz);
      const qs = params.toString() ? `?${params.toString()}` : "";
      return request<MobileTodayFeedResponse>(
        "GET",
        `/api/partner/mobile/posts/today${qs}`
      );
    },

    async postRange(from: string, to: string, tz?: string) {
      const qs = new URLSearchParams({ from, to });
      if (tz) qs.set("tz", tz);
      return request<MobilePostRangeResponse>(
        "GET",
        `/api/partner/mobile/posts/range?${qs.toString()}`
      );
    },

    async calendarSummary(month: string, tz?: string) {
      const [y, m] = month.split("-").map(Number);
      const from = `${month}-01`;
      const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
      const to = `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
      // Build the query string via plain string concatenation rather than
      // URLSearchParams.  On shipped Hermes builds (iOS 1.8.1/1.8.2) the
      // URLSearchParams polyfill can mangle YYYY-MM-DD values, producing a
      // garbled or empty query string that the server rejects with a 400.
      // Both `from` and `to` are pre-validated YYYY-MM-DD strings so they
      // never contain characters that need percent-encoding.
      let qs = `from=${from}&to=${to}`;
      if (tz) qs += `&tz=${encodeURIComponent(tz)}`;
      // Sanity-guard: if the built URL somehow lost `from=`, log it so
      // the corruption is visible in the console / OS log before the request goes out.
      if (!qs.startsWith("from=")) {
        console.warn("[calendarSummary] qs:corrupt", qs.slice(0, 100));
      }
      return request<MobilePostRangeResponse>(
        "GET",
        `/api/partner/mobile/posts/range?${qs}`
      );
    },

    async reconnectUrl(
      platform: string | undefined,
      returnTo: string,
      requestedPlatformOrProvider?: string,
      options?: MobileReconnectUrlOptions
    ) {
      const provider =
        options?.provider ??
        (requestedPlatformOrProvider === "instagram_login" ||
        requestedPlatformOrProvider === "facebook_login"
          ? requestedPlatformOrProvider
          : undefined);
      const requestedPlatform =
        options?.requestedPlatform ??
        (provider !== undefined ? undefined : requestedPlatformOrProvider);
      // InlineURLSearchParams in release-like Hermes runtimes ignores record
      // constructor arguments. Populate explicitly so the native callback is
      // never dropped while `.set()` remains supported.
      const qs = new URLSearchParams();
      qs.set("returnTo", returnTo);
      if (platform) qs.set("platform", platform);
      if (requestedPlatform) qs.set("requestedPlatform", requestedPlatform);
      if (provider) qs.set("provider", provider);
      if (options?.restaurantId) qs.set("restaurantId", options.restaurantId);
      if (options?.manage) qs.set("manage", "1");
      return request<{ url: string }>(
        "GET",
        `/api/partner/mobile/reconnect-url?${qs.toString()}`,
        undefined,
        options?.restaurantId
          ? { "x-restaurant-id": options.restaurantId }
          : undefined
      );
    },

    async listScheduled() {
      return request<MobileScheduledFeedResponse>(
        "GET",
        "/api/partner/mobile/scheduled"
      );
    },

    async cancelScheduled(postId: string) {
      return request<{ success: boolean; postId: string; status: string }>(
        "DELETE",
        `/api/partner/mobile/scheduled/${postId}`
      );
    },

    async deletePost(postId: string) {
      return request<{ success: true; wasScheduled: boolean }>(
        "DELETE",
        `/api/partner/mobile/posts/${postId}`
      );
    },

    async rescheduleScheduled(postId: string, localScheduledFor: string) {
      return request<{
        success: boolean;
        postId: string;
        scheduledFor: string;
      }>("PATCH", `/api/partner/mobile/scheduled/${postId}`, {
        localScheduledFor,
      });
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Public factory
// ─────────────────────────────────────────────────────────────────────────────

export function createApiClient(config: ApiClientConfig): ApiClient {
  const { request, requestRaw, _refreshKudoz, _refreshEatzy } =
    buildCoreHttp(config);

  return {
    config,
    storage: config.storage,
    auth: createAuthModule(config, request, _refreshKudoz, _refreshEatzy),
    user: createUserModule(request),
    partner: {
      posts: createPartnerPostsModule(config, request, requestRaw),
      menu: createPartnerMenuModule(request),
      audio: createPartnerAudioModule(request),
      restaurants: createPartnerRestaurantsModule(request),
      notifications: createPartnerNotificationsModule(request),
      dashboard: createPartnerDashboardModule(request),
      social: createPartnerSocialModule(request),
      video: createPartnerVideoModule(config, request),
      mobilePublish: createMobilePublishModule(request),
    },
    account: createAccountModule(request),
    media: createMediaModule(config, request),
    shortLinks: createShortLinksModule(request),
    raw: request,
  };
}

export interface SocialPlatformHealth {
  platform: string;
  connected: boolean;
  needsReconnect: boolean;
  authorizationProvider?: SocialAuthorizationProvider | null;
  providerAccountType?: SocialProviderAccountType | null;
  accountId?: string | null;
  name?: string | null;
  handle?: string | null;
  capabilities?: SocialProviderCapabilities | null;
  recoveryReason?: MetaRecoveryReason | null;
  connectionProvenance?: MetaConnectionProvenance | null;
  reconnectPlatform?: "facebook" | "instagram" | "tiktok" | null;
}

export type MetaConnectionProvenance =
  | "direct"
  | "facebook_linked"
  | "legacy_unknown";
